import React, { useState, useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  MapPin, Plus, Play, CheckCircle, Trash2, X, Edit,
  Calendar, Clock, Camera, Image, Video, Eye, ChevronLeft, ChevronRight,
  User, Phone, Filter, Navigation, Maximize2,
  ArrowUp, CornerUpLeft, CornerUpRight, ArrowLeft, ArrowRight, Flag, FileText
} from 'lucide-react'
import api from '../../Includes/api/api'
import { bindKarnatakaAutocomplete, geocodePlace, liveLocationIcon, loadGoogleMaps, reverseGeocode, watchLiveLocation } from '../../Includes/googleMaps'
import Swal from 'sweetalert2'
import { set } from 'date-fns'
import { useSignupAdmin } from '../../Includes/signupAdmin'

// Returns only the school/college name (text before first comma)
const schoolName = (areaName) => (areaName || '').split(',')[0].trim()

const isSupportVisit = (visit) => (
  Boolean(visit?.is_support_visit)
  || String(visit?.notes || '').startsWith('Support visit')
  || String(visit?.notes || '').startsWith('Visit type: Support')
)

const SupportVisitChip = () => (
  <span className="chip chip-slate" style={{ marginLeft: 6 }}>Support</span>
)

const formatVisitDate = (value) => {
  if (!value) return '—'
  const day = String(value).substring(0, 10)
  const [year, month, date] = day.split('-').map(Number)
  if (!year || !month || !date) return '—'
  return new Date(year, month - 1, date).toLocaleDateString()
}

const CREATED_DATE_PALETTE = [
  { bg: '#dbeafe', fg: '#1d4ed8', border: '#93c5fd' },
  { bg: '#dcfce7', fg: '#15803d', border: '#86efac' },
  { bg: '#fef3c7', fg: '#b45309', border: '#fcd34d' },
  { bg: '#fce7f3', fg: '#be185d', border: '#f9a8d4' },
  { bg: '#ede9fe', fg: '#6d28d9', border: '#c4b5fd' },
  { bg: '#cffafe', fg: '#0e7490', border: '#67e8f9' },
  { bg: '#ffedd5', fg: '#c2410c', border: '#fdba74' },
  { bg: '#e0e7ff', fg: '#4338ca', border: '#a5b4fc' },
  { bg: '#fae8ff', fg: '#a21caf', border: '#f0abfc' },
  { bg: '#ccfbf1', fg: '#0f766e', border: '#5eead4' },
  { bg: '#fee2e2', fg: '#b91c1c', border: '#fca5a5' },
  { bg: '#fef9c3', fg: '#a16207', border: '#fde047' },
]

const createdDateColor = (value) => {
  if (!value) return null
  const [year, month, date] = String(value).substring(0, 10).split('-').map(Number)
  if (!year || !month || !date) return null
  const dayNumber = Math.floor(Date.UTC(year, month - 1, date) / 86400000)
  const index = ((dayNumber % CREATED_DATE_PALETTE.length) + CREATED_DATE_PALETTE.length) % CREATED_DATE_PALETTE.length
  return CREATED_DATE_PALETTE[index]
}

function CreatedDateLabel({ value }) {
  const color = createdDateColor(value)
  if (!color) return '—'
  return (
    <span
      className="created-date-label"
      style={{ background: color.bg, color: color.fg, borderColor: color.border }}
    >
      <Calendar size={13} />
      {formatVisitDate(value)}
    </span>
  )
}

const loadGooglePlaces = loadGoogleMaps

function GoogleLocationSearch({ onPlace, getOrigin }) {
  const inputRef = useRef(null)
  const onPlaceRef = useRef(onPlace)
  const getOriginRef = useRef(getOrigin)
  useEffect(() => { onPlaceRef.current = onPlace }, [onPlace])
  useEffect(() => { getOriginRef.current = getOrigin }, [getOrigin])
  useEffect(() => {
    let cancelled = false
    loadGooglePlaces().then((maps) => {
      if (cancelled || !inputRef.current) return
      bindKarnatakaAutocomplete(maps, inputRef.current, (place) => {
        onPlaceRef.current({
          latitude: Number(place.lat).toFixed(7),
          longitude: Number(place.lng).toFixed(7),
          address: place.address || place.name || '',
        })
      }, () => getOriginRef.current?.())
    }).catch(() => {})
    return () => {
      cancelled = true
      inputRef.current?._karnatakaCleanup?.()
    }
  }, [])
  return (
    <input
      ref={inputRef}
      type="text"
      className="form-control"
      placeholder="Private schools, colleges, or locations in Karnataka"
      style={{ width: '100%', marginBottom: 10 }}
    />
  )
}

function GoogleClickMap({ center, marker, onPick, height = 360 }) {
  const nodeRef = useRef(null)
  const mapRef = useRef(null)
  const markerRef = useRef(null)
  const onPickRef = useRef(onPick)
  const [ready, setReady] = useState(false)
  useEffect(() => { onPickRef.current = onPick }, [onPick])
  useEffect(() => {
    let cancelled = false
    loadGooglePlaces().then((maps) => {
      if (cancelled || !nodeRef.current || mapRef.current) return
      const map = new maps.Map(nodeRef.current, {
        center: { lat: Number(center?.[0] ?? 12.9716), lng: Number(center?.[1] ?? 77.5946) },
        zoom: 14,
        mapTypeControl: false,
        streetViewControl: false,
        gestureHandling: 'greedy',
      })
      mapRef.current = map
      setReady(true)
      map.addListener('click', (event) => {
        const position = { lat: event.latLng.lat(), lng: event.latLng.lng() }
        if (!markerRef.current) markerRef.current = new maps.Marker({ map, position, draggable: true })
        else markerRef.current.setPosition(position)
        onPickRef.current?.({
          latitude: position.lat.toFixed(7),
          longitude: position.lng.toFixed(7),
        })
      })
    }).catch(() => {})
    return () => { cancelled = true }
  }, [])
  useEffect(() => {
    const map = mapRef.current
    const maps = window.google?.maps
    if (!map || !maps || !center) return
    const position = { lat: Number(center[0]), lng: Number(center[1]) }
    map.panTo(position)
    if (!marker) return
    if (!markerRef.current) markerRef.current = new maps.Marker({ map, position, draggable: true })
    else markerRef.current.setPosition(position)
  }, [center, marker, ready])
  return <div ref={nodeRef} style={{ height: '100%', width: '100%', minHeight: height }} />
}

const EMPTY_VISIT_FORM = {
  person: '', area_name: '',
  planned_date: new Date().toLocaleDateString('en-CA'), notes: '',
  latitude: '', longitude: '',
  contact_number: '', interest_status: 'pending',
  wants_parents_orientation: false,
  wants_student_orientation: false,
  wants_staff_orientation: false,
  wants_revisit: false,
  interested_services: ''
}

const formatStepDistance = (meters) => {
  if (meters == null) return ''
  if (meters >= 1000) return `${(meters / 1000).toFixed(1)} km`
  return `${Math.round(meters)} m`
}

const SCHOOL_DEST_KEY = (visitId) => `visit_school_dest_${visitId}`

const saveSchoolDestination = (visit) => {
  if (!visit?.id) return
  const lat = visit.latitude != null && visit.latitude !== '' ? parseFloat(visit.latitude) : null
  const lng = visit.longitude != null && visit.longitude !== '' ? parseFloat(visit.longitude) : null
  if (lat == null || lng == null || Number.isNaN(lat) || Number.isNaN(lng)) return
  try {
    localStorage.setItem(SCHOOL_DEST_KEY(visit.id), JSON.stringify({
      lat, lng, area_name: visit.area_name || '',
    }))
  } catch { /* ignore */ }
}

const loadSchoolDestination = (visitId) => {
  try {
    const raw = JSON.parse(localStorage.getItem(SCHOOL_DEST_KEY(visitId)) || 'null')
    if (raw?.lat == null || raw?.lng == null) return null
    const lat = parseFloat(raw.lat)
    const lng = parseFloat(raw.lng)
    if (Number.isNaN(lat) || Number.isNaN(lng)) return null
    return { lat, lng, area_name: raw.area_name || '' }
  } catch { return null }
}

const coordsNearlyEqual = (aLat, aLng, bLat, bLng, eps = 0.0003) => {
  if (aLat == null || aLng == null || bLat == null || bLng == null) return false
  return Math.abs(aLat - bLat) < eps && Math.abs(aLng - bLng) < eps
}

/** Geocode school/area name: saved institutes first, then Google Maps. */
const geocodeDestination = async (query, origin, label) => {
  const baseQuery = (query || '').trim()
  if (!baseQuery) throw new Error('No destination for this visit')

  const schoolOnly = baseQuery.split(',')[0].trim()
  const queries = [
    baseQuery,
    schoolOnly !== baseQuery ? schoolOnly : null,
    `${schoolOnly}, Bengaluru, Karnataka, India`,
    `${schoolOnly}, Bangalore, India`,
    baseQuery.includes('India') ? null : `${baseQuery}, India`,
  ].filter(Boolean)

  const seen = new Set()
  const uniqueQueries = queries.filter((q) => {
    const k = q.toLowerCase()
    if (seen.has(k)) return false
    seen.add(k)
    return true
  })

  // 1) Our stored schools/colleges DB (most reliable for planned visit schools)
  try {
    const res = await api.get('/api/area-search/stored/', { params: { q: schoolOnly } })
    const list = Array.isArray(res.data?.data) ? res.data.data : []
    if (list.length) {
      const lower = schoolOnly.toLowerCase()
      const exact = list.find((s) => (s.name || '').toLowerCase() === lower)
      const starts = list.find((s) => (s.name || '').toLowerCase().startsWith(lower))
      const hit = exact || starts || list[0]
      if (hit?.lat != null && hit?.lng != null) {
        return { lat: parseFloat(hit.lat), lng: parseFloat(hit.lng), label: hit.name || label }
      }
    }
  } catch { /* continue */ }

  void origin
  for (const q of uniqueQueries) {
    try {
      const hit = await geocodePlace(q)
      if (hit && !Number.isNaN(hit.lat) && !Number.isNaN(hit.lng)) return { ...hit, label }
    } catch { /* try next query */ }
  }
  throw new Error('Could not find destination on map. Open the visit and set the school pin on the map, then try again.')
}

const formatManeuverLabel = (step) => {
  const type = (step.maneuver?.type || '').replace(/_/g, ' ')
  const modifier = (step.maneuver?.modifier || '').replace(/_/g, ' ')
  const road = step.name || ''
  if (type === 'arrive') return road ? `Arrive at ${road}` : 'You have arrived'
  if (type === 'depart') return road ? `Head towards ${road}` : 'Head to destination'
  if (modifier && road) return `${modifier} onto ${road}`
  if (modifier) return modifier
  if (type && road) return `${type} ${road}`
  return road || type || 'Continue'
}

const ManeuverIcon = ({ step, size = 28 }) => {
  const type = step?.maneuver?.type || ''
  const modifier = step?.maneuver?.modifier || ''
  if (type === 'arrive') return <Flag size={size} />
  if (modifier.includes('left') || type.includes('left')) {
    if (modifier.includes('sharp')) return <ArrowLeft size={size} />
    return <CornerUpLeft size={size} />
  }
  if (modifier.includes('right') || type.includes('right')) {
    if (modifier.includes('sharp')) return <ArrowRight size={size} />
    return <CornerUpRight size={size} />
  }
  return <ArrowUp size={size} />
}

/** In-app turn-by-turn navigation drawn on Google Maps. */
function InAppDirectionsScreen({ route, fullscreen, onClose }) {
  const mapNodeRef = useRef(null)
  const leafletMapRef = useRef(null)
  const routeLineRef = useRef(null)
  const [steps, setSteps] = useState([])
  const [eta, setEta] = useState({ mins: null, km: null, arriveAt: '' })
  const [loading, setLoading] = useState(true)
  const [mapError, setMapError] = useState('')

  const destroyMap = () => {
    leafletMapRef.current = null
    routeLineRef.current = null
    if (mapNodeRef.current) mapNodeRef.current.innerHTML = ''
  }

  const refreshMapSize = () => {
    const map = leafletMapRef.current
    const line = routeLineRef.current
    const maps = window.google?.maps
    if (!map || !line || !maps) return
    const bounds = new maps.LatLngBounds()
    line.getPath().forEach((point) => bounds.extend(point))
    map.fitBounds(bounds, 80)
  }

  useEffect(() => {
    const timers = [80, 250, 500, 900].map((ms) => setTimeout(refreshMapSize, ms))
    return () => timers.forEach(clearTimeout)
  }, [fullscreen])

  useEffect(() => {
    let cancelled = false
    let resizeObserver = null
    let stopLive = () => {}

    const resolveDestination = async () => {
      const origin = (route.originLat != null && route.originLng != null)
        ? { lat: route.originLat, lng: route.originLng }
        : null
      const label = route.schoolName || 'Destination'

      const remember = (dest) => {
        if (route.visitId != null && dest?.lat != null && dest?.lng != null) {
          saveSchoolDestination({
            id: route.visitId,
            latitude: dest.lat,
            longitude: dest.lng,
            area_name: route.destQuery || route.schoolName,
          })
          // Persist school pin back onto the visit if it was overwritten by GPS
          api.patch(`/visits/${route.visitId}/`, {
            latitude: dest.lat,
            longitude: dest.lng,
          }).catch(() => {})
        }
        return dest
      }

      // 1) Explicit destination coords that are not the same as current GPS
      if (route.destLat != null && route.destLng != null
        && !Number.isNaN(route.destLat) && !Number.isNaN(route.destLng)) {
        if (!origin || !coordsNearlyEqual(route.destLat, route.destLng, origin.lat, origin.lng)) {
          return remember({ lat: route.destLat, lng: route.destLng, label })
        }
      }

      // 2) Cached school destination (saved before Start Ride overwrote coords)
      const cached = route.visitId != null ? loadSchoolDestination(route.visitId) : null
      if (cached && (!origin || !coordsNearlyEqual(cached.lat, cached.lng, origin.lat, origin.lng))) {
        return remember({ lat: cached.lat, lng: cached.lng, label })
      }

      // 3) Lookup school DB / geocode name
      const q = route.destQuery || route.schoolName
      const found = await geocodeDestination(q, origin, label)
      return remember(found)
    }

    const ensureMap = async (center) => {
      destroyMap()
      if (!mapNodeRef.current) return null
      const maps = await loadGooglePlaces()
      if (cancelled || !mapNodeRef.current) return null
      const map = new maps.Map(mapNodeRef.current, {
        center,
        zoom: 15,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        gestureHandling: 'greedy',
      })
      leafletMapRef.current = map
      return { map, maps }
    }

    const run = async () => {
      setLoading(true)
      setMapError('')
      setSteps([])
      setEta({ mins: null, km: null, arriveAt: '' })
      try {
        if (route.originLat == null || route.originLng == null) {
          throw new Error('Current GPS location is required for directions')
        }
        const origin = { lat: route.originLat, lng: route.originLng }
        const dest = await resolveDestination()
        if (cancelled || !mapNodeRef.current) return

        const ready = await ensureMap(origin)
        if (!ready) return
        const { map, maps } = ready
        const liveMarker = { current: null }
        liveMarker.current = new maps.Marker({
          map,
          position: origin,
          zIndex: 2000,
          title: 'Live location',
          icon: liveLocationIcon(maps),
        })
        stopLive = watchLiveLocation(map, maps, liveMarker)
        if (cancelled) {
          stopLive()
          return
        }

        if (typeof ResizeObserver !== 'undefined' && mapNodeRef.current) {
          resizeObserver = new ResizeObserver(() => refreshMapSize())
          resizeObserver.observe(mapNodeRef.current)
        }

        const url = `https://router.project-osrm.org/route/v1/driving/${origin.lng},${origin.lat};${dest.lng},${dest.lat}?overview=full&geometries=geojson&steps=true`
        const resp = await fetch(url)
        const data = await resp.json()
        if (cancelled) return
        if (data.code !== 'Ok' || !data.routes?.[0]) throw new Error('Could not build route')

        const routeData = data.routes[0]
        const path = (routeData.geometry?.coordinates || []).map(([lng, lat]) => ({ lat, lng }))
        new maps.Marker({
          map,
          position: { lat: dest.lat, lng: dest.lng },
          label: 'B',
          title: dest.label || 'Destination',
        })

        if (path.length) {
          const line = new maps.Polyline({
            map,
            path,
            strokeColor: '#1a73e8',
            strokeWeight: 6,
            strokeOpacity: 0.95,
          })
          routeLineRef.current = line
          const bounds = new maps.LatLngBounds()
          path.forEach((point) => bounds.extend(point))
          map.fitBounds(bounds, 80)
        }

        const km = (routeData.distance / 1000).toFixed(1)
        const mins = Math.max(1, Math.round(routeData.duration / 60))
        const arrive = new Date(Date.now() + routeData.duration * 1000)
        const arriveAt = arrive.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
        setEta({ mins, km, arriveAt })

        const stepList = []
        ;(routeData.legs || []).forEach((leg) => {
          ;(leg.steps || []).forEach((step) => {
            if (step.maneuver?.type === 'arrive' && !step.name) {
              stepList.push({
                ...step,
                label: `Arrive at ${route.schoolName}`,
                distanceLabel: formatStepDistance(step.distance),
              })
              return
            }
            stepList.push({
              ...step,
              label: formatManeuverLabel(step),
              distanceLabel: formatStepDistance(step.distance),
            })
          })
        })
        setSteps(stepList)
        ;[60, 200, 450, 800].forEach((ms) => {
          setTimeout(() => { if (!cancelled) refreshMapSize() }, ms)
        })
      } catch (err) {
        if (!cancelled) setMapError(err.message || 'Failed to load directions')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    run()
    return () => {
      cancelled = true
      stopLive()
      if (resizeObserver) resizeObserver.disconnect()
      destroyMap()
    }
  }, [route])

  const nextStep = steps.find((s) => s.maneuver?.type !== 'depart') || steps[0]
  const thenStep = steps.length > 1
    ? steps.find((s, i) => i > 0 && s.maneuver?.type !== 'arrive') || steps[1]
    : null

  return (
    <div className="inapp-nav">
      {!loading && !mapError && nextStep && (
        <div className="inapp-nav-banner">
          <div className="inapp-nav-banner-main">
            <span className="inapp-nav-banner-icon"><ManeuverIcon step={nextStep} size={32} /></span>
            <div className="inapp-nav-banner-text">
              <span className="inapp-nav-banner-dist">{nextStep.distanceLabel}</span>
              <span className="inapp-nav-banner-label">{nextStep.label}</span>
            </div>
          </div>
          {thenStep && (
            <div className="inapp-nav-banner-then">
              <span>Then</span>
              <ManeuverIcon step={thenStep} size={16} />
            </div>
          )}
        </div>
      )}

      <div ref={mapNodeRef} className="inapp-nav-map" />

      <div className="inapp-nav-bottom">
        <button type="button" className="inapp-nav-close" onClick={onClose} title="Close">
          <X size={20} />
        </button>
        <div className="inapp-nav-eta">
          {loading ? (
            <div className="inapp-nav-eta-loading">Loading route…</div>
          ) : mapError ? (
            <div className="inapp-nav-eta-error">{mapError}</div>
          ) : (
            <>
              <div className="inapp-nav-eta-mins">{eta.mins != null ? `${eta.mins} min` : '—'}</div>
              <div className="inapp-nav-eta-meta">
                {[eta.km != null ? `${eta.km} km` : null, eta.arriveAt].filter(Boolean).join(' · ')}
                {route.schoolName ? ` · ${route.schoolName}` : ''}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

const monthRangeFromValue = (value) => {
  const [yearText, monthText] = String(value || '').split('-')
  const year = Number(yearText)
  const monthIndex = Number(monthText) - 1
  const start = new Date(year, monthIndex, 1)
  const end = new Date(year, monthIndex + 1, 0)
  const iso = (date) => {
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, '0')
    const d = String(date.getDate()).padStart(2, '0')
    return `${y}-${m}-${d}`
  }
  return { from: iso(start), to: iso(end), month: `${yearText}-${monthText}` }
}

const mapsQueryUrl = (lat, lng, fallbackName) => {
  if (lat != null && lng != null && !Number.isNaN(Number(lat)) && !Number.isNaN(Number(lng))) {
    return `https://www.google.com/maps/search/?api=1&query=${Number(lat)},${Number(lng)}`
  }
  const name = (fallbackName || '').trim()
  if (!name) return ''
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(name)}`
}

const dataUrlToBlob = (dataUrl) => {
  const [header, b64] = dataUrl.split(',')
  const mime = (header.match(/:(.*?);/) || [])[1] || 'image/jpeg'
  const binary = atob(b64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i)
  return new Blob([bytes], { type: mime })
}

const reachedSchoolMessage = ({ school, lat, lng, when }) => {
  const lines = [`Reached school: ${school || 'School'}`]
  if (lat != null && lng != null && !Number.isNaN(Number(lat)) && !Number.isNaN(Number(lng))) {
    lines.push(`Coordinates: ${Number(lat).toFixed(6)}, ${Number(lng).toFixed(6)}`)
  }
  const mapUrl = mapsQueryUrl(lat, lng, school)
  if (mapUrl) lines.push(`Map: ${mapUrl}`)
  lines.push(`Date & time: ${when.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}`)
  return lines.join('\n')
}

const stampPhotoCaption = (ctx, width, height, lines) => {
  const fontSize = Math.max(13, Math.round(width / 32))
  ctx.font = `600 ${fontSize}px sans-serif`
  const pad = 8
  const lineHeight = fontSize + 5
  const boxHeight = lines.length * lineHeight + pad * 2
  ctx.fillStyle = 'rgba(15, 23, 42, 0.75)'
  ctx.fillRect(0, height - boxHeight, width, boxHeight)
  ctx.fillStyle = '#ffffff'
  const maxWidth = width - pad * 2
  lines.forEach((line, i) => {
    let text = line
    while (text.length > 8 && ctx.measureText(text).width > maxWidth) {
      text = `${text.slice(0, -2)}…`
    }
    ctx.fillText(text, pad, height - boxHeight + pad + fontSize + i * lineHeight)
  })
}

const shareOnWhatsApp = async ({ file, text }) => {
  try {
    if (file && navigator.canShare?.({ files: [file] })) {
      await navigator.share({ files: [file], text, title: 'Reached school' })
      return
    }
  } catch (err) {
    if (err?.name === 'AbortError') return
  }
  window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener')
}

const visitPoint = (visit) => {
  const cached = loadSchoolDestination(visit.id)
  const lat = cached?.lat != null ? cached.lat : parseFloat(visit.latitude)
  const lng = cached?.lng != null ? cached.lng : parseFloat(visit.longitude)
  if (Number.isNaN(lat) || Number.isNaN(lng)) return null
  return { lat, lng }
}

/** Road path through one day's visits, in the order they were planned. */
function DayVisitPathMap({ title, stops, onClose }) {
  const mapNodeRef = useRef(null)
  const [summary, setSummary] = useState('')
  const [mapError, setMapError] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    let stopLive = () => {}
    const node = mapNodeRef.current
    if (!node) return undefined
    if (!stops.length) {
      setLoading(false)
      setMapError('No school locations for this day.')
      return undefined
    }
    setMapError('')
    setLoading(true)
    setSummary('')

    const run = async () => {
      let map
      let maps
      try {
        maps = await loadGooglePlaces()
        if (cancelled) return
        map = new maps.Map(node, {
          center: { lat: stops[0].lat, lng: stops[0].lng },
          zoom: 12,
          mapTypeControl: false,
          streetViewControl: false,
          gestureHandling: 'greedy',
        })
        stopLive = watchLiveLocation(map, maps, { current: null })
        if (cancelled) stopLive()
        const info = new maps.InfoWindow()
        const numberIcon = (n) => ({
          url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28"><circle cx="14" cy="14" r="12" fill="#1a73e8" stroke="white" stroke-width="2"/><text x="14" y="18" text-anchor="middle" fill="white" font-size="12" font-family="Arial" font-weight="700">${n}</text></svg>`)}`,
          scaledSize: new maps.Size(28, 28),
          anchor: new maps.Point(14, 14),
        })
        stops.forEach((stop, i) => {
          const marker = new maps.Marker({
            map,
            position: { lat: stop.lat, lng: stop.lng },
            icon: numberIcon(i + 1),
            title: stop.name,
          })
          marker.addListener('click', () => {
            info.setContent(`<strong>${i + 1}. ${stop.name}</strong>`)
            info.open(map, marker)
          })
        })
        const fitPath = (path) => {
          const bounds = new maps.LatLngBounds()
          path.forEach((point) => bounds.extend(point))
          map.fitBounds(bounds, 36)
        }
        const drawStraight = () => {
          const path = stops.map((stop) => ({ lat: stop.lat, lng: stop.lng }))
          new maps.Polyline({ map, path, strokeColor: '#1a73e8', strokeWeight: 4, strokeOpacity: 0.85 })
          fitPath(path)
        }
        const coordPath = stops.map((stop) => `${stop.lng},${stop.lat}`).join(';')
        const resp = await fetch(
          `https://router.project-osrm.org/route/v1/driving/${coordPath}?overview=full&geometries=geojson`
        )
        const data = await resp.json()
        if (cancelled) return
        const geometry = data.routes?.[0]?.geometry?.coordinates
        if (data.code !== 'Ok' || !geometry?.length) {
          drawStraight()
          setSummary('Road route unavailable. Showing the planned stop order.')
          return
        }
        const route = data.routes[0]
        const path = geometry.map(([lng, lat]) => ({ lat, lng }))
        new maps.Polyline({ map, path, strokeColor: '#1a73e8', strokeWeight: 5, strokeOpacity: 0.9 })
        fitPath(path)
        const km = (route.distance / 1000).toFixed(1)
        const mins = Math.max(1, Math.round(route.duration / 60))
        setSummary(`${stops.length} stops · ${km} km · about ${mins} min`)
      } catch {
        if (!cancelled) {
          setSummary('Road route unavailable. Showing the planned stop order.')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    run()
    return () => {
      cancelled = true
      stopLive()
      node.innerHTML = ''
    }
  }, [stops])

  return (
    <div className="modal-overlay" style={{ zIndex: 1500 }} onClick={onClose}>
      <div className="modal" style={{ maxWidth: 920, width: '96%' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h3 className="modal-title">Visit path</h3>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>{title}</div>
          </div>
          <button type="button" className="modal-close" onClick={onClose}><X size={20} /></button>
        </div>
        <div className="modal-body" style={{ padding: 0 }}>
          <div ref={mapNodeRef} style={{ height: '62vh', minHeight: 320, width: '100%', background: '#e8eef5' }} />
          <div style={{ padding: '10px 16px', fontSize: 13, color: 'var(--text-secondary)' }}>
            {loading ? 'Drawing the day’s route…' : (mapError || summary || `${stops.length} stops`)}
            {' · blue dot is your live location'}
          </div>
        </div>
      </div>
    </div>
  )
}

const FOLLOWUP_DEFAULT_DAYS = 3

const defaultFollowupDate = () => {
  const d = new Date()
  d.setDate(d.getDate() + FOLLOWUP_DEFAULT_DAYS)
  return d.toLocaleDateString('en-CA')
}

export default function VisitsPage() {
  const isAdmin = useSignupAdmin()
  const location = useLocation()
  const navigate = useNavigate()
  const myVisitsOnly = location.pathname === '/sales/my-visits'
  const [visitKind, setVisitKind] = useState('sales')
  const [visits, setVisits] = useState([])
  const [homeVisit, setHomeVisit] = useState(null)
  const [lunchVisit, setLunchVisit] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [persons, setPersons] = useState([])
  const [loading, setLoading] = useState(true)
  const [showAddModal, setShowAddModal] = useState(false)
  const [nearbySchools, setNearbySchools] = useState([])
  const [nearbyLoading, setNearbyLoading] = useState(false)
  const [nearbyError, setNearbyError] = useState('')
  const [nearbyQuery, setNearbyQuery] = useState('')
  const [currentLocation, setCurrentLocation] = useState(null)
  const [selectedNearbyKey, setSelectedNearbyKey] = useState('')
  const [editingVisitId, setEditingVisitId] = useState(null)
  const [form, setForm] = useState(EMPTY_VISIT_FORM)
  const [searchCenter, setSearchCenter] = useState(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [locatingGPS, setLocatingGPS] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [filterDateFrom, setFilterDateFrom] = useState(() => new Date().toLocaleDateString('en-CA'))
  const [filterDateTo, setFilterDateTo] = useState(() => new Date().toLocaleDateString('en-CA'))
  const [filterPerson, setFilterPerson] = useState('')
  const [filterTransport, setFilterTransport] = useState('')
  const [statusMsg, setStatusMsg] = useState('')
  const [serviceInput, setServiceInput] = useState('')
  const [masterServices, setMasterServices] = useState(() => {
    const saved = localStorage.getItem('edubricz_custom_services');
    return saved ? JSON.parse(saved) : [];
  })
  const [showAddServiceModal, setShowAddServiceModal] = useState(false)
  const [newServiceName, setNewServiceName] = useState('')
  const [showMoveModal, setShowMoveModal] = useState(false)
  const [dayPath, setDayPath] = useState(null)
  const [moveDate, setMoveDate] = useState(() => new Date().toLocaleDateString('en-CA'))
  const [movePerDay, setMovePerDay] = useState(6)
  const [movingVisits, setMovingVisits] = useState(false)
  const [locationPick, setLocationPick] = useState(null)
  const [savingLocation, setSavingLocation] = useState(false)


  // Reset searchCenter when modal opens/closes
  useEffect(() => {
    if (!showAddModal) {
      setSearchCenter(null)
      setEditingVisitId(null)
    }
  }, [showAddModal])

  const searchParams = new URLSearchParams(location.search)
  const defaultStatus = searchParams.get('status') || ''

  const [filterStatus, setFilterStatus] = useState(defaultStatus)
  const [showCamera, setShowCamera] = useState(false)
  // 'media' = optional photo + interest form; 'school_start' / 'school_end' = optional checkpoint photo
  const [cameraPurpose, setCameraPurpose] = useState('media')
  const [detailsOnly, setDetailsOnly] = useState(false)
  // Capture button: upload the photo only, never touch visit status / interest details
  const [photoOnly, setPhotoOnly] = useState(false)
  const [capturedPhoto, setCapturedPhoto] = useState(null)
  const [captureForm, setCaptureForm] = useState({
    interest_status: '',
    contact_number: '',
    strength:'',
    interested_services: [],
    other_interests: '',
    not_interested_reason: '',
    main_branch_search_query: '',
    next_visit_plan_date: new Date().toLocaleDateString('en-CA'),
    followup_date: defaultFollowupDate(),
    // followup_purpose: '',
    wants_parents_orientation: false,
    wants_student_orientation: false,
    wants_staff_orientation: false,
    wants_revisit: false
  })
  // { visit, loading, saving, companyId, companyName, items, feedback }
  const [supportEnd, setSupportEnd] = useState(null)
  const [selectedVisit, setSelectedVisit] = useState(null)
  const [showMediaModal, setShowMediaModal] = useState(false)
  const [media, setMedia] = useState([])
  const [previewMedia, setPreviewMedia] = useState(null)
  const [academicYears, setAcademicYears] = useState([])
  const [selectedAcademicYear, setSelectedAcademicYear] = useState('')
  const [followups, setFollowups] = useState([])
  const [showFollowupModal, setShowFollowupModal] = useState(false)
  const [followupForm, setFollowupForm] = useState({
    visit: '',
    academic_year: '',
    followup_date: '',
    next_purpose: '',
    notes: ''
  })
  const [branchSuggestions, setBranchSuggestions] = useState([])
  const [fetchingBranchSuggestions, setFetchingBranchSuggestions] = useState(false)
  const [showBranchDropdown, setShowBranchDropdown] = useState(false)
  const [activeRoute, setActiveRoute] = useState(null)
  const [routePanelOpen, setRoutePanelOpen] = useState(false)
  const [routeFullscreen, setRouteFullscreen] = useState(false)
  const [routeMountKey, setRouteMountKey] = useState(0)
  const videoRef = useRef(null)
  const streamRef = useRef(null)
  const locationRef = useRef(null)
  const capturingVisitRef = useRef(null)
  const pendingRideEndRef = useRef(null) // { visit, payload } while optional end-ride photo is open
  const lastPathPostRef = useRef({}) // visitId → last posted {lat,lng} for path persistence

  const openRouteGuide = (visit, origin) => {
    if (!visit) return
    // Prefer cached school destination (survives Start Ride GPS overwrite)
    const cached = loadSchoolDestination(visit.id)
    let destLat = cached?.lat ?? null
    let destLng = cached?.lng ?? null
    if (destLat == null || destLng == null) {
      destLat = visit.latitude != null && visit.latitude !== '' ? parseFloat(visit.latitude) : null
      destLng = visit.longitude != null && visit.longitude !== '' ? parseFloat(visit.longitude) : null
    }
    // If visit lat/lng is actually current GPS (same as origin), ignore it
    const originLat = origin?.lat != null ? parseFloat(origin.lat) : null
    const originLng = origin?.lng != null ? parseFloat(origin.lng) : null
    if (coordsNearlyEqual(destLat, destLng, originLat, originLng)) {
      destLat = null
      destLng = null
    } else if (destLat != null && destLng != null && !Number.isNaN(destLat) && !Number.isNaN(destLng)) {
      saveSchoolDestination({ ...visit, latitude: destLat, longitude: destLng })
    }

    const hasCoords = destLat != null && destLng != null && !Number.isNaN(destLat) && !Number.isNaN(destLng)
    const destQuery = visit.area_name || schoolName(visit.area_name) || ''
    if (!hasCoords && !destQuery) return
    setActiveRoute({
      visitId: visit.id,
      schoolName: schoolName(visit.area_name) || visit.area_name || 'Destination',
      originLat,
      originLng,
      destLat: hasCoords ? destLat : null,
      destLng: hasCoords ? destLng : null,
      destQuery,
    })
    setRouteMountKey((k) => k + 1)
    setRoutePanelOpen(true)
    setRouteFullscreen(true)
  }

  const closeRouteGuide = () => {
    setRoutePanelOpen(false)
    setRouteFullscreen(false)
  }

  const toggleRouteGuide = (visit) => {
    if (activeRoute?.visitId === visit.id && routePanelOpen) {
      setRoutePanelOpen(false)
      setRouteFullscreen(false)
      return
    }
    const origin = locationRef.current?.latitude
      ? { lat: locationRef.current.latitude, lng: locationRef.current.longitude }
      : (() => {
          try {
            const saved = JSON.parse(localStorage.getItem(`visit_last_gps_${visit.id}`) || 'null')
            return saved?.lat != null ? { lat: saved.lat, lng: saved.lng } : null
          } catch { return null }
        })()
    openRouteGuide(visit, origin)
  }

  // Lock body scroll while route map is fullscreen
  useEffect(() => {
    if (!routeFullscreen) return undefined
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [routeFullscreen])

  // Close fullscreen with Escape
  useEffect(() => {
    if (!routeFullscreen) return undefined
    const onKey = (e) => {
      if (e.key === 'Escape') setRouteFullscreen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [routeFullscreen])

  // Haversine distance calculator between 2 coordinates in KM
  const haversineDistance = (lat1, lon1, lat2, lon2) => {
    if (!lat1 || !lon1 || !lat2 || !lon2) return 0
    const R = 6371 // Earth radius in km
    const dLat = (parseFloat(lat2) - parseFloat(lat1)) * (Math.PI / 180)
    const dLon = (parseFloat(lon2) - parseFloat(lon1)) * (Math.PI / 180)
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(parseFloat(lat1) * (Math.PI / 180)) * Math.cos(parseFloat(lat2) * (Math.PI / 180)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    return parseFloat((R * c).toFixed(2))
  }

  const persistPathPoint = (visit, lat, lng, accuracy, phase) => {
    const key = String(visit.id)
    const last = lastPathPostRef.current[key]
    if (last) {
      const step = haversineDistance(last.lat, last.lng, lat, lng)
      if (step < 0.025) return // ignore jitter < 25m
    }
    lastPathPostRef.current[key] = { lat, lng }
    api.post('/visits/locations/', {
      visit: visit.id,
      latitude: parseFloat(lat).toFixed(7),
      longitude: parseFloat(lng).toFixed(7),
      accuracy: accuracy ?? null,
      phase: phase || (visit.status === 'riding_home' ? 'ride_home' : 'to_school'),
    }).catch(() => {})
  }

  // Shows distance dynamically
  const getVisitDistance = (visit) => {
    if (visit.status === 'planned') {
      return '0.0' // Has not started yet
    }
    if (visit.status === 'in_progress' || visit.status === 'riding_home') {
      const storageKey = visit.status === 'riding_home' ? `visit_home_km_${visit.id}` : `visit_km_${visit.id}`
      return liveDistances[visit.id] || localStorage.getItem(storageKey) || '0.0'
    }
    // Ride ended / at school / completed — use saved distance (to-school + optional home)
    const toSchool = parseFloat(visit.distance_km || 0)
    const homeKm = parseFloat(visit.home_distance_km || 0)
    const total = toSchool + homeKm
    if (total > 0) return total.toFixed(1)
    return localStorage.getItem(`visit_km_${visit.id}`) || '0.0'
  }

  const formatTimeSpent = (minutes) => {
    if (minutes == null || minutes === '' || Number.isNaN(Number(minutes))) return '—'
    const mins = Math.max(0, parseInt(minutes, 10))
    if (mins === 0) return '0m'
    const hrs = Math.floor(mins / 60)
    const rem = mins % 60
    return hrs > 0 ? `${hrs}h ${rem}m` : `${rem}m`
  }

  const getLiveSchoolMinutes = (visit) => {
    if (visit.status !== 'at_school' || !visit.school_arrival_time) return null
    const start = new Date(visit.school_arrival_time).getTime()
    if (Number.isNaN(start)) return null
    return Math.max(0, Math.floor((Date.now() - start) / 60000))
  }

  // Calculates total bike travel for the day (including in-progress unended visits)
  const getPersonDayDistance = (visitsOnDay) => {
    let totalKm = 0
    visitsOnDay.forEach(v => {
      if (v.status === 'in_progress') {
        totalKm += parseFloat(liveDistances[v.id] || localStorage.getItem(`visit_km_${v.id}`) || 0)
      } else if (v.status === 'riding_home') {
        totalKm += parseFloat(v.distance_km || 0)
        totalKm += parseFloat(liveDistances[v.id] || localStorage.getItem(`visit_home_km_${v.id}`) || 0)
      } else {
        totalKm += parseFloat(v.distance_km || 0)
        totalKm += parseFloat(v.home_distance_km || 0)
        if (!v.distance_km) {
          totalKm += parseFloat(localStorage.getItem(`visit_km_${v.id}`) || 0)
        }
      }
    })
    return totalKm.toFixed(1)
  }

  const getPersonDayBusCharge = (visitsOnDay) => {
    let totalCharge = 0
    visitsOnDay.forEach(v => {
      if (v.transport_mode === 'bus') {
        totalCharge += parseFloat(v.bus_charge || 0)
      }
      if (v.home_transport_mode === 'bus') {
        totalCharge += parseFloat(v.home_bus_charge || 0)
      }
    })
    return totalCharge.toFixed(0)
  }



  // Calculates total bike travel for the day (including in-progress unended visits)
  const [bikeTravelMonth, setBikeTravelMonth] = useState(() => {
    const d = new Date()
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
  })
  const [monthlyBikeTravelKm, setMonthlyBikeTravelKm] = useState('0.0')
  const [monthlyBusCharge, setMonthlyBusCharge] = useState('0')
  const [fetchingBikeTravel, setFetchingBikeTravel] = useState(false)
  const [calculatedMonthLabel, setCalculatedMonthLabel] = useState(
    new Date().toLocaleString('en-US', { month: 'long', year: 'numeric' }).toUpperCase()
  )

  useEffect(() => {
    if (!isAdmin) return
    let isMounted = true
    const fetchMonthlyTravel = async () => {
      setFetchingBikeTravel(true)
      try {
        const params = { page_size: 10000 }
        if (selectedAcademicYear) params.academic_year_id = selectedAcademicYear
        const res = await api.get('/visits/', { params })
        if (!isMounted) return
        const rawData = res.data
        let data = rawData.results || rawData.data || rawData || []

        let targetMonth, targetYear

        if (bikeTravelMonth) {
          const [yearStr, monthStr] = bikeTravelMonth.split('-')
          targetYear = parseInt(yearStr, 10)
          targetMonth = parseInt(monthStr, 10) - 1

          const parsedDate = new Date(targetYear, targetMonth, 1)
          setCalculatedMonthLabel(parsedDate.toLocaleString('en-US', { month: 'long', year: 'numeric' }).toUpperCase())
        } else {
          const d = new Date()
          targetMonth = d.getMonth()
          targetYear = d.getFullYear()
          setCalculatedMonthLabel(d.toLocaleString('en-US', { month: 'long', year: 'numeric' }).toUpperCase())
        }

        const thisMonthVisits = data.filter(v => {
          if (!v.planned_date) return false
          const d = new Date(v.planned_date)
          return d.getMonth() === targetMonth && d.getFullYear() === targetYear
        })

        const byPersonDay = {}
        thisMonthVisits.forEach(v => {
          const pId = v.person_detail?.id || v.person || 'unknown'
          const day = v.planned_date.substring(0, 10)
          const key = `${pId}_${day}`
          if (!byPersonDay[key]) byPersonDay[key] = []
          byPersonDay[key].push(v)
        })

        let total = 0
        let totalBus = 0
        Object.values(byPersonDay).forEach(group => {
          group.forEach(v => {
            if (v.transport_mode === 'bus') {
              totalBus += parseFloat(v.bus_charge || 0)
            } else {
              if (v.status === 'inprogress') {
                total += parseFloat(localStorage.getItem(`visit_km_${v.id}`) || 0)
              } else if (v.distance_km && parseFloat(v.distance_km) > 0) {
                total += parseFloat(v.distance_km)
              } else {
                total += parseFloat(localStorage.getItem(`visit_km_${v.id}`) || 0)
              }
            }
            if (v.home_transport_mode === 'bus') {
              totalBus += parseFloat(v.home_bus_charge || 0)
            } else if (v.home_distance_km && parseFloat(v.home_distance_km) > 0) {
              total += parseFloat(v.home_distance_km)
            }
          })
        })
        setMonthlyBikeTravelKm(total.toFixed(1))
        setMonthlyBusCharge(totalBus.toFixed(0))
      } catch (e) {
        console.error("Failed to fetch monthly travel", e)
      } finally {
        if (isMounted) setFetchingBikeTravel(false)
      }
    }

    if (!selectedAcademicYear) return undefined
    fetchMonthlyTravel()
    return () => {
      isMounted = false
    }
  }, [bikeTravelMonth, isAdmin, visits, selectedAcademicYear])



  const fetchVisits = async () => {
    setLoading(true)
    try {
      const params = {}
      if (myVisitsOnly) {
        params.mine = '1'
      }
      if (filterStatus && filterStatus !== 'followups') {
        params.status = filterStatus
      }
      if (selectedAcademicYear) {
        params.academic_year_id = selectedAcademicYear
      }
      if (filterPerson) {
        params.person_id = filterPerson
      }
      if (filterDateFrom) {
        params.planned_date_from = filterDateFrom
      }
      if (filterDateTo) {
        params.planned_date_to = filterDateTo
      }
      if (filterTransport) {
        params.transport_mode = filterTransport
      }

      params.page = currentPage
      params.page_size = itemsPerPage
      params.visit_kind = visitKind
      const res = await api.get('/visits/', { params })
      const rawData = res.data
      let data = []
      if (rawData && Array.isArray(rawData.data)) {
        data = rawData.data
      } else if (rawData && Array.isArray(rawData.results)) {
        data = rawData.results
        if (rawData.count !== undefined) {
          setTotalItems(rawData.count)
          setTotalPages(Math.ceil(rawData.count / itemsPerPage))
        } else {
          setTotalPages(1)
        }
      } else if (Array.isArray(rawData)) {
        data = rawData
        setTotalPages(1)
      }
      let rows = data
      if (myVisitsOnly) {
        const today = new Date().toLocaleDateString('en-CA')
        try {
          const commuteRes = await api.get('/visits/', {
            params: {
              mine: '1',
              planned_date_from: today,
              planned_date_to: today,
              page_size: 100,
              visit_kind: 'sales',
              ...(selectedAcademicYear ? { academic_year_id: selectedAcademicYear } : {}),
            },
          })
          const commuteRows = Array.isArray(commuteRes.data?.data)
            ? commuteRes.data.data
            : Array.isArray(commuteRes.data?.results)
              ? commuteRes.data.results
              : []
          const commute = commuteRows.find((visit) => visit.area_name === 'From Home')
          if (commute) {
            setHomeVisit(commute)
            if (!rows.some((visit) => visit.id === commute.id)) rows = [...rows, commute]
          }
          const todayLunch = commuteRows.find((visit) => visit.area_name === 'Lunch Break' && visit.status !== 'visited')
            || commuteRows.find((visit) => visit.area_name === 'Lunch Break')
          setLunchVisit((current) => {
            if (todayLunch) return todayLunch
            const stillOpen = current?.area_name === 'Lunch Break'
              && current.status !== 'visited'
              && String(current.planned_date || current.start_time || '').startsWith(today)
            return stillOpen ? current : null
          })
          if (todayLunch && !rows.some((visit) => visit.id === todayLunch.id)) rows = [...rows, todayLunch]
        } catch { /* header falls back to the loaded page */ }
      }
      setVisits(rows)
      // Cache school destinations for planned visits (before Start Ride can overwrite coords)
      rows.forEach((v) => {
        if (v.status === 'planned') saveSchoolDestination(v)
        else if ((v.status === 'in_progress' || v.status === 'arrived') && !loadSchoolDestination(v.id)) {
          saveSchoolDestination(v)
        }
      })
    } catch { setError('Failed to load visits.') }
    finally { setLoading(false) }
  }

  const openDayPath = async (dateStr) => {
    const label = new Date(`${dateStr}T00:00:00`).toLocaleDateString('en-US', {
      weekday: 'long', month: 'short', day: 'numeric', year: 'numeric',
    })
    setDayPath({ title: label, stops: [], loading: true })
    try {
      const params = { planned_date: dateStr, page: 1, page_size: 500, visit_kind: visitKind }
      if (myVisitsOnly) params.mine = '1'
      if (filterStatus && filterStatus !== 'followups') params.status = filterStatus
      if (selectedAcademicYear) params.academic_year_id = selectedAcademicYear
      if (filterPerson) params.person_id = filterPerson
      const res = await api.get('/visits/', { params })
      const rawData = res.data
      const data = Array.isArray(rawData?.data)
        ? rawData.data
        : Array.isArray(rawData?.results)
          ? rawData.results
          : Array.isArray(rawData) ? rawData : []
      const ordered = [...data].sort((a, b) => {
        const timeA = a.created_at ? new Date(a.created_at).getTime() : 0
        const timeB = b.created_at ? new Date(b.created_at).getTime() : 0
        if (timeA !== timeB) return timeA - timeB
        return (Number(a.id) || 0) - (Number(b.id) || 0)
      })
      const stops = []
      for (const visit of ordered) {
        if (visit.area_name === 'From Home' || visit.area_name === 'Lunch Break') continue
        let point = visitPoint(visit)
        if (!point) {
          try {
            const found = await geocodeDestination(visit.area_name, null, schoolName(visit.area_name))
            if (found?.lat != null && found?.lng != null) point = { lat: found.lat, lng: found.lng }
          } catch { /* skip stops we cannot place */ }
        }
        if (!point) continue
        stops.push({ id: visit.id, ...point, name: schoolName(visit.area_name) || 'School' })
      }
      setDayPath({ title: label, stops, loading: false })
    } catch {
      setDayPath({ title: label, stops: [], loading: false })
    }
  }

  const fetchPersons = async () => {
    try {
      const res = await api.get('/persons/')
      const rawData = res.data
      let data = []
      if (rawData && Array.isArray(rawData.data)) {
        data = rawData.data
      } else if (rawData && Array.isArray(rawData.results)) {
        data = rawData.results
      } else if (Array.isArray(rawData)) {
        data = rawData
      }
      setPersons(data)
    } catch { }
  }

  const fetchAcademicYears = async () => {
    try {
      const res = await api.get('/basic/academicyear/')
      const rawData = res.data
      let years = []
      if (rawData && Array.isArray(rawData.data)) {
        years = rawData.data
      } else if (rawData && Array.isArray(rawData.results)) {
        years = rawData.results
      } else if (Array.isArray(rawData)) {
        years = rawData
      }

      setAcademicYears(years)

      // Find active year and set as default
      const activeYear = years.find(y => y.is_active)
      if (activeYear) {
        setSelectedAcademicYear(String(activeYear.id))
      } else if (years.length > 0) {
        setSelectedAcademicYear(String(years[0].id))
      }
    } catch (err) {
      console.error("Failed to load academic years:", err)
      setLoading(false)
    }
  }

  const fetchFollowups = async () => {
    if (!selectedAcademicYear) return
    setLoading(true)
    try {
      const res = await api.get('/visits/followups/', {
        params: { academic_year_id: selectedAcademicYear }
      })
      const rawData = res.data
      let data = []
      if (rawData && Array.isArray(rawData.data)) {
        data = rawData.data
      } else if (rawData && Array.isArray(rawData.results)) {
        data = rawData.results
      } else if (Array.isArray(rawData)) {
        data = rawData
      }
      setFollowups(data)
    } catch (err) {
      console.error("Failed to load follow-ups:", err)
      setError("Failed to load follow-up records.")
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteFollowup = async (followupId) => {
    if (!window.confirm('Delete this follow-up record?')) return
    try {
      await api.delete(`/visits/followups/${followupId}/`)
      fetchFollowups()
    } catch {
      alert('Failed to delete follow-up.')
    }
  }

  const openFollowupModal = (visit) => {
    setFollowupForm({
      visit: visit.id,
      academic_year: selectedAcademicYear || '',
      followup_date: defaultFollowupDate(),
      next_purpose: '',
      notes: ''
    })
    setError('')
    setShowFollowupModal(true)
  }

  const handleCreateFollowup = async (e) => {
    e.preventDefault()
    if (!followupForm.visit || !followupForm.academic_year || !followupForm.followup_date || !followupForm.next_purpose) {
      setError('Academic Year, Date, and Purpose are required.')
      return
    }
    setSaving(true)
    setError('')
    try {
      await api.post('/visits/followups/', followupForm)
      setShowFollowupModal(false)
      if (filterStatus === 'followups') {
        fetchFollowups()
      } else {
        setFilterStatus('followups')
      }
    } catch (err) {
      console.error("Failed to log follow-up:", err)
      setError(err.response?.data?.error || 'Failed to log follow-up.')
    } finally {
      setSaving(false)
    }
  }

  useEffect(() => {
    const q = captureForm.main_branch_search_query?.trim()
    if (!q || q.length < 2 || !showBranchDropdown) {
      setBranchSuggestions([])
      return
    }
    const timer = setTimeout(async () => {
      setFetchingBranchSuggestions(true)
      try {
        const res = await api.get('/api/area-search/suggest/', { params: { input: q } })
        const list = res.data?.suggestions || []
        setBranchSuggestions(list)
      } catch (err) {
        console.error("Failed to fetch branch suggestions:", err)
        setBranchSuggestions([])
      } finally {
        setFetchingBranchSuggestions(false)
      }
    }, 350)
    return () => clearTimeout(timer)
  }, [captureForm.main_branch_search_query, showBranchDropdown])


  useEffect(() => {
    fetchPersons()
    fetchAcademicYears()
  }, [filterStatus, selectedAcademicYear, currentPage, itemsPerPage, filterDateFrom, filterDateTo, filterTransport])

  useEffect(() => {
    if (!selectedAcademicYear) return
    if (filterStatus === 'followups' && visitKind === 'sales') {
      fetchFollowups()
    } else {
      fetchVisits()
    }
  }, [filterStatus, selectedAcademicYear, currentPage, itemsPerPage, filterPerson, filterDateFrom, filterDateTo, filterTransport, myVisitsOnly, visitKind])

  useEffect(() => {
    let watchId = null
    if (navigator.geolocation) {
      // Prompt for location permission on mount and keep track of it dynamically
      watchId = navigator.geolocation.watchPosition(
        (position) => {
          locationRef.current = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          }
          console.log("Location pre-fetched:", locationRef.current)
        },
        (error) => {
          console.error("Error pre-fetching location:", error)
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      )
    }
    return () => {
      if (watchId !== null) {
        navigator.geolocation.clearWatch(watchId)
      }
    }
  }, [])

  const loadNearbySchools = async (lat, lng) => {
    setNearbyLoading(true)
    setNearbyError('')
    try {
      const res = await api.post('/api/area-search/nearby/', {
        lat: Number(lat),
        lng: Number(lng),
        radius_km: 5,
      })
      const schools = Array.isArray(res.data?.schools) ? res.data.schools : []
      setNearbySchools(schools)
      if (!schools.length) {
        setNearbyError('No schools within 5 km. Add a new one using the current location or the map.')
      }
    } catch {
      setNearbySchools([])
      setNearbyError('Could not load nearby schools. You can still add one by location.')
    } finally {
      setNearbyLoading(false)
    }
  }

  const rememberCurrentLocation = (lat, lng) => {
    const la = Number(lat)
    const ln = Number(lng)
    if (Number.isNaN(la) || Number.isNaN(ln)) return
    setCurrentLocation({ lat: la, lng: ln })
    setSearchCenter([la, ln])
    setLocatingGPS(false)
    loadNearbySchools(la, ln)
  }

  const useCurrentLocationForSchool = () => {
    const here = currentLocation || (locationRef.current
      ? { lat: Number(locationRef.current.latitude), lng: Number(locationRef.current.longitude) }
      : null)
    if (!here) return
    setSelectedNearbyKey('')
    setForm((f) => ({
      ...f,
      latitude: here.lat.toFixed(7),
      longitude: here.lng.toFixed(7),
    }))
    setSearchCenter([here.lat, here.lng])
  }

  const selectNearbySchool = (school) => {
    const rawName = school.address ? `${school.name}, ${school.address}` : school.name
    const areaName = rawName.length > 300 ? `${rawName.slice(0, 297)}...` : rawName
    const key = String(school.id || `${school.name}-${school.lat}-${school.lng}`)
    setSelectedNearbyKey(key)
    setForm((f) => ({
      ...f,
      area_name: areaName,
      latitude: Number(school.lat).toFixed(7),
      longitude: Number(school.lng).toFixed(7),
      contact_number: school.phone || f.contact_number || '',
    }))
    if (school.lat != null && school.lng != null) {
      setSearchCenter([Number(school.lat), Number(school.lng)])
    }
  }

  const openAddModal = () => {
    setForm(EMPTY_VISIT_FORM)
    setEditingVisitId(null)
    setError('')
    setShowAddModal(true)
    setSearchQuery('')
    setNearbySchools([])
    setNearbyError('')
    setNearbyQuery('')
    setSelectedNearbyKey('')
    setCurrentLocation(null)

    if (locationRef.current) {
      rememberCurrentLocation(locationRef.current.latitude, locationRef.current.longitude)
    } else if (navigator.geolocation) {
      setLocatingGPS(true)
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          locationRef.current = { latitude: pos.coords.latitude, longitude: pos.coords.longitude }
          rememberCurrentLocation(pos.coords.latitude, pos.coords.longitude)
        },
        (err) => {
          console.warn('Auto GPS failed:', err)
          setLocatingGPS(false)
          setNearbyError('Current location is unavailable. Search or click the map to add a school.')
        },
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 10000 }
      )
    }
  }

  const openEditModal = (visit) => {
    setForm({
      person: visit.person,
      area_name: visit.area_name,
      planned_date: visit.planned_date || '',
      notes: visit.notes || '',
      latitude: visit.latitude ? parseFloat(visit.latitude).toFixed(7) : '',
      longitude: visit.longitude ? parseFloat(visit.longitude).toFixed(7) : '',
      contact_number: visit.contact_number || '',
      interest_status: visit.interest_status || 'pending',
      wants_parents_orientation: visit.wants_parents_orientation || false,
      wants_student_orientation: visit.wants_student_orientation || false,
      wants_staff_orientation: visit.wants_staff_orientation || false,
      wants_revisit: visit.wants_revisit || false,
      interested_services: visit.interested_services || ''
    })
    setEditingVisitId(visit.id)
    setError('')
    setSearchQuery('')
    if (visit.latitude && visit.longitude) {
      // Visit already has saved coords — use them
      setSearchCenter([parseFloat(visit.latitude), parseFloat(visit.longitude)])
      setLocatingGPS(false)
    } else if (locationRef.current) {
      // Use pre-fetched live location instantly
      const lat = parseFloat(locationRef.current.latitude).toFixed(7)
      const lng = parseFloat(locationRef.current.longitude).toFixed(7)
      setForm(f => ({ ...f, latitude: lat, longitude: lng }))
      setSearchCenter([parseFloat(lat), parseFloat(lng)])
      setLocatingGPS(false)
    } else if (navigator.geolocation) {
      // Fallback: fetch fresh
      setSearchCenter(null)
      setLocatingGPS(true)
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const lat = parseFloat(pos.coords.latitude).toFixed(7)
          const lng = parseFloat(pos.coords.longitude).toFixed(7)
          locationRef.current = { latitude: pos.coords.latitude, longitude: pos.coords.longitude }
          setForm(f => ({ ...f, latitude: lat, longitude: lng }))
          setSearchCenter([parseFloat(lat), parseFloat(lng)])
          setLocatingGPS(false)
        },
        (err) => {
          console.warn('Auto GPS failed:', err)
          setLocatingGPS(false)
        },
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 10000 }
      )
    } else {
      setSearchCenter(null)
    }
    setShowAddModal(true)
  }

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }))
  }

  const handleAddVisit = async (e) => {
    e.preventDefault()
    if (!selectedAcademicYear) {
      setError('Select an academic year before saving a visit.')
      return
    }
    if (editingVisitId && !form.person) {
      setError('Person is required.')
      return
    }
    if (!form.area_name) {
      setError('School/College Name is required.')
      return
    }
    setSaving(true)
    setError('')
    try {
      const payload = {
        ...form,
        planned_date: form.planned_date || null,
        academic_year: selectedAcademicYear || null,
      }
      if (!editingVisitId) {
        delete payload.person
        try {
          const user = JSON.parse(localStorage.getItem('user') || '{}')
          if (user?.id) payload.user_id = user.id
        } catch { /* the server assigns the logged-in person */ }
      }
      if (editingVisitId) {
        await api.put(`/visits/${editingVisitId}/`, payload)
      } else {
        await api.post('/visits/', payload)
      }

      setShowAddModal(false)
      setEditingVisitId(null)
      setForm(EMPTY_VISIT_FORM)
      fetchVisits()
    } catch (err) {
      setError(err.response?.data?.area_name?.[0] || 'Failed to save visit.')
    } finally {
      setSaving(false)
    }
  }

  const handleMoveVisits = async (e) => {
    e.preventDefault()
    const perDay = Number(movePerDay)
    if (!moveDate) {
      setError('Choose the date to start planning from.')
      return
    }
    if (!Number.isInteger(perDay) || perDay < 1) {
      setError('Institutes per day must be at least 1.')
      return
    }
    setMovingVisits(true)
    setError('')
    try {
      const res = await api.post('/visits/move-unvisited/', {
        start_date: moveDate,
        per_day: perDay,
        academic_year_id: selectedAcademicYear || null,
      })
      const moved = res.data?.moved || 0
      setShowMoveModal(false)
      const range = monthRangeFromValue(String(moveDate).slice(0, 7))
      const monthChanged = filterDateFrom !== range.from || filterDateTo !== range.to
      setFilterDateFrom(range.from)
      setFilterDateTo(range.to)
      setCurrentPage(1)
      if (!monthChanged) fetchVisits()
      Swal.fire({
        icon: moved ? 'success' : 'info',
        title: moved ? 'Visits planned' : 'Nothing to move',
        text: res.data?.message || (moved
          ? `Planned ${moved} unvisited visit(s).`
          : 'No unvisited visits from previous days.'),
      })
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to move visits.')
    } finally {
      setMovingVisits(false)
    }
  }

  const [liveDistances, setLiveDistances] = useState({})
  const [, setSchoolTick] = useState(0)

  // Refresh "Time in School" display every 30s while any visit is at_school
  useEffect(() => {
    const hasAtSchool = visits.some(v => v.status === 'at_school')
    if (!hasAtSchool) return undefined
    const id = setInterval(() => setSchoolTick(t => t + 1), 30000)
    return () => clearInterval(id)
  }, [visits])

  // 1. Live GPS tracking while riding to school or riding home — persist path points
  useEffect(() => {
    const trackingVisits = visits.filter(v => v.status === 'in_progress' || v.status === 'riding_home')
    if (trackingVisits.length === 0 || !navigator.geolocation) return

    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const curLat = pos.coords.latitude
        const curLng = pos.coords.longitude
        const accuracy = pos.coords.accuracy

        trackingVisits.forEach(v => {
          const isHome = v.status === 'riding_home'
          const kmKey = isHome ? `visit_home_km_${v.id}` : `visit_km_${v.id}`
          const lastKey = isHome ? `visit_home_last_gps_${v.id}` : `visit_last_gps_${v.id}`
          const lastRaw = localStorage.getItem(lastKey)
          const curTotal = parseFloat(localStorage.getItem(kmKey) || '0')

          if (lastRaw) {
            try {
              const lastGps = JSON.parse(lastRaw)
              const stepDist = haversineDistance(lastGps.lat, lastGps.lng, curLat, curLng)

              // Only accumulate if moved at least 25 meters to ignore GPS jitter
              if (stepDist >= 0.025) {
                const newTotal = parseFloat((curTotal + stepDist).toFixed(2))
                localStorage.setItem(kmKey, String(newTotal))
                localStorage.setItem(lastKey, JSON.stringify({ lat: curLat, lng: curLng }))
                setLiveDistances(prev => ({ ...prev, [v.id]: newTotal }))
                persistPathPoint(v, curLat, curLng, accuracy, isHome ? 'ride_home' : 'to_school')
              }
            } catch (e) { /* ignore */ }
          } else {
            localStorage.setItem(lastKey, JSON.stringify({ lat: curLat, lng: curLng }))
            persistPathPoint(v, curLat, curLng, accuracy, isHome ? 'ride_home' : 'to_school')
          }
        })
      },
      (err) => console.warn("GPS live watch error:", err),
      { enableHighAccuracy: true, maximumAge: 3000, timeout: 10000 }
    )

    return () => navigator.geolocation.clearWatch(watchId)
  }, [visits])

  // 1.5. GPS tracking while AT SCHOOL — update every 15 minutes
  useEffect(() => {
    const atSchoolVisits = visits.filter(v => v.status === 'at_school')
    if (atSchoolVisits.length === 0 || !navigator.geolocation) return

    const intervalId = setInterval(() => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const curLat = pos.coords.latitude
          const curLng = pos.coords.longitude
          const accuracy = pos.coords.accuracy

          atSchoolVisits.forEach(v => {
            persistPathPoint(v, curLat, curLng, accuracy, 'at_school')
          })
        },
        (err) => console.warn("GPS at school interval error:", err),
        { enableHighAccuracy: true, maximumAge: 0, timeout: 10000 }
      )
    }, 900000) // 15 minutes

    return () => clearInterval(intervalId)
  }, [visits])

  const HOME_COMMUTE = 'From Home'

  const isHomeCommute = (visit) => visit?.area_name === HOME_COMMUTE

  const readGpsNow = async () => {
    if (locationRef.current?.latitude) {
      return {
        latitude: parseFloat(locationRef.current.latitude).toFixed(7),
        longitude: parseFloat(locationRef.current.longitude).toFixed(7),
        accuracy: 100,
      }
    }
    if (!navigator.geolocation) {
      throw new Error('Your device or browser does not support GPS.')
    }
    const position = await new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 })
    })
    return {
      latitude: position.coords.latitude.toFixed(7),
      longitude: position.coords.longitude.toFixed(7),
      accuracy: position.coords.accuracy,
    }
  }

  const rememberHomeVisit = (visit) => {
    if (!visit?.id) return
    setHomeVisit(visit)
    setVisits((prev) => {
      const index = prev.findIndex((row) => row.id === visit.id)
      if (index === -1) return [...prev, visit]
      const next = [...prev]
      next[index] = { ...next[index], ...visit }
      return next
    })
  }

  const todayHomeCommute = () => {
    const todayStr = new Date().toLocaleDateString('en-CA')
    const matchesToday = (visit) => isHomeCommute(visit) && (
      visit.planned_date === todayStr
      || visit.visited_date === todayStr
      || (visit.start_time && String(visit.start_time).startsWith(todayStr))
    )
    const stepRank = (visit) => {
      if (visit?.home_end_time) return 4
      if (visit?.status === 'riding_home') return 3
      if (visit?.status === 'visited' || visit?.status === 'arrived') return 2
      if (visit?.status === 'in_progress') return 1
      return 0
    }
    const candidates = visits.filter(matchesToday)
    if (homeVisit && matchesToday(homeVisit)) {
      const index = candidates.findIndex((visit) => visit.id === homeVisit.id)
      if (index === -1) candidates.push(homeVisit)
      else if (stepRank(homeVisit) > stepRank(candidates[index])) {
        candidates[index] = { ...candidates[index], ...homeVisit }
      }
    }
    return candidates.sort((a, b) => stepRank(b) - stepRank(a))[0] || null
  }

  const handleStartFromHome = async () => {
    const activeVisits = visits.filter((visit) => (
      !isHomeCommute(visit) && (visit.status === 'in_progress' || visit.status === 'at_school' || visit.status === 'riding_home')
    ))
    if (activeVisits.length > 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Finish the current ride',
        text: activeVisits.map((visit) => visit.area_name).join(', '),
      })
      return
    }
    let gps
    try {
      Swal.fire({ title: 'Starting from home...', allowOutsideClick: false, didOpen: () => Swal.showLoading() })
      gps = await readGpsNow()
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'GPS Required', text: err.message || 'Turn on location and try again.' })
      return
    }
    try {
      setSaving(true)
      const user = JSON.parse(localStorage.getItem('user') || '{}')
      const existing = todayHomeCommute()
      let visitId = existing?.status === 'planned' ? existing.id : null
      if (!visitId) {
        const created = await api.post('/visits/', {
          user_id: user?.id || undefined,
          area_name: HOME_COMMUTE,
          status: 'planned',
          planned_date: new Date().toLocaleDateString('en-CA'),
          academic_year: selectedAcademicYear || null,
        })
        visitId = created.data?.id
      }
      if (!visitId) throw new Error('Could not start from home.')
      localStorage.setItem(`visit_start_gps_${visitId}`, JSON.stringify({ lat: gps.latitude, lng: gps.longitude }))
      localStorage.setItem(`visit_last_gps_${visitId}`, JSON.stringify({ lat: Number(gps.latitude), lng: Number(gps.longitude) }))
      localStorage.setItem(`visit_km_${visitId}`, '0.0')
      setLiveDistances((prev) => ({ ...prev, [visitId]: '0.0' }))
      const started = await api.post(`/visits/${visitId}/start_visit/`, gps)
      rememberHomeVisit(started.data?.id ? started.data : {
        id: visitId,
        area_name: HOME_COMMUTE,
        status: 'in_progress',
        planned_date: new Date().toLocaleDateString('en-CA'),
        start_time: new Date().toISOString(),
        home_end_time: null,
      })
      fetchVisits()
      Swal.fire({
        icon: 'success',
        title: 'Started from home',
        text: 'Tap Reached location when you arrive.',
        timer: 1800,
        showConfirmButton: false,
      })
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Failed', text: err.response?.data?.error || err.message || 'Could not start from home.' })
    } finally {
      setSaving(false)
    }
  }

  const handleReachedLocation = async (visit) => {
    const finalDistance = parseFloat(liveDistances[visit.id] || localStorage.getItem(`visit_km_${visit.id}`) || 0)
    const transportDetails = await askTransportDetails('Reached location')
    if (!transportDetails) return
    try {
      setSaving(true)
      const gps = await readGpsNow().catch(() => ({}))
      Swal.fire({ title: 'Saving home to location...', allowOutsideClick: false, didOpen: () => Swal.showLoading() })
      await api.post(`/visits/${visit.id}/end_visit/`, {
        distance_km: finalDistance,
        transport_mode: transportDetails.transport_mode,
        bus_charge: transportDetails.bus_charge,
        ...gps,
      })
      const saved = await api.patch(`/visits/${visit.id}/`, {
        status: 'visited',
        visited_date: new Date().toLocaleDateString('en-CA'),
      })
      rememberHomeVisit(saved.data?.id ? saved.data : {
        ...visit,
        status: 'visited',
        visited_date: new Date().toLocaleDateString('en-CA'),
        home_end_time: null,
        home_start_time: null,
      })
      localStorage.removeItem(`visit_km_${visit.id}`)
      localStorage.removeItem(`visit_last_gps_${visit.id}`)
      setLiveDistances((prev) => {
        const next = { ...prev }
        delete next[visit.id]
        return next
      })
      fetchVisits()
      Swal.fire({
        icon: 'success',
        title: 'Reached location',
        text: `Home to location: ${finalDistance.toFixed(1)} km saved to expenses.`,
        timer: 2000,
        showConfirmButton: false,
      })
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Failed', text: err.response?.data?.error || 'Could not save the home to location distance.' })
    } finally {
      setSaving(false)
    }
  }

  const renderHomeCommuteButton = () => {
    const commute = todayHomeCommute()
    const buttonStyle = {
      padding: '6px 12px',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      color: '#fff',
    }
    if (!commute || commute.status === 'planned') {
      return (
        <button className="btn btn-secondary" style={{ ...buttonStyle, background: '#2563eb', borderColor: '#1d4ed8' }} onClick={handleStartFromHome} disabled={saving}>
          <Play size={14} /> Start from home
        </button>
      )
    }
    if (commute.status === 'in_progress') {
      return (
        <button className="btn btn-secondary" style={{ ...buttonStyle, background: '#d97706', borderColor: '#b45309' }} onClick={() => handleReachedLocation(commute)} disabled={saving}>
          <CheckCircle size={14} /> Reached location
        </button>
      )
    }
    if (commute.status === 'riding_home') {
      return (
        <button className="btn btn-secondary" style={{ ...buttonStyle, background: '#d97706', borderColor: '#b45309' }} onClick={() => handleEndRideHome(commute)} disabled={saving}>
          <CheckCircle size={14} /> Reached home
        </button>
      )
    }
    if (!commute.home_end_time) {
      return (
        <button className="btn btn-secondary" style={{ ...buttonStyle, background: '#16a34a', borderColor: '#15803d' }} onClick={() => beginRideHome(commute)} disabled={saving}>
          <Play size={14} /> Ride home
        </button>
      )
    }
    return (
      <button className="btn btn-secondary" style={{ ...buttonStyle, background: '#f1f5f9', color: '#64748b', borderColor: '#cbd5e1', cursor: 'not-allowed' }} disabled>
        Home done
      </button>
    )
  }

  // Lunch Break Actions
  const handleStartLunch = async () => {
    try {
      setSaving(true)
      const user = JSON.parse(localStorage.getItem('user') || '{}')
      const payload = {
        user_id: user?.id || undefined,
        area_name: 'Lunch Break',
        status: 'in_progress',
        start_time: new Date().toISOString(),
        planned_date: new Date().toLocaleDateString('en-CA'),
        academic_year: selectedAcademicYear || null
      }
      const created = await api.post('/visits/', payload)
      const started = created.data?.id ? created.data : {
        area_name: 'Lunch Break',
        status: 'in_progress',
        planned_date: payload.planned_date,
        start_time: payload.start_time,
      }
      setLunchVisit(started)
      setVisits((prev) => (
        prev.some((visit) => visit.id === started.id)
          ? prev.map((visit) => (visit.id === started.id ? { ...visit, ...started } : visit))
          : [...prev, started]
      ))
      fetchVisits()
      Swal.fire({ icon: 'success', title: 'Lunch Break Started', timer: 1500, showConfirmButton: false })
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Could not start lunch break.' })
    } finally {
      setSaving(false)
    }
  }

  const handleEndLunch = async (visit) => {
    try {
      setSaving(true)
      const now = new Date().toISOString()
      const payload = {
        status: 'visited',
        school_arrival_time: now,
        end_time: now,
        visited_date: new Date().toLocaleDateString('en-CA')
      }
      const ended = await api.patch(`/visits/${visit.id}/`, payload)
      const finished = ended.data?.id ? ended.data : { ...visit, ...payload }
      setLunchVisit(finished)
      setVisits((prev) => prev.map((row) => (row.id === visit.id ? { ...row, ...finished } : row)))
      fetchVisits()
      Swal.fire({ icon: 'success', title: 'Lunch Break Ended', timer: 1500, showConfirmButton: false })
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Could not end lunch break.' })
    } finally {
      setSaving(false)
    }
  }

  const renderLunchBreakButton = () => {
    const todayStr = new Date().toLocaleDateString('en-CA')
    const isTodayLunch = (visit) => visit?.area_name === 'Lunch Break' && (
      String(visit.planned_date || '').startsWith(todayStr)
      || String(visit.visited_date || '').startsWith(todayStr)
      || String(visit.start_time || '').startsWith(todayStr)
    )
    const fromList = visits.find(isTodayLunch)
    const todayLunch = (lunchVisit && isTodayLunch(lunchVisit) && lunchVisit.status !== 'visited')
      ? lunchVisit
      : (fromList || (lunchVisit && isTodayLunch(lunchVisit) ? lunchVisit : null))

    if (!todayLunch) {
      return (
        <button className="btn btn-secondary" style={{ background: '#f59e0b', color: '#fff', borderColor: '#d97706', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: '6px' }} onClick={handleStartLunch} disabled={saving}>
          🥪 Start Lunch
        </button>
      )
    } else if (todayLunch.status !== 'visited') {
      return (
        <button className="btn btn-secondary" style={{ background: '#10b981', color: '#fff', borderColor: '#059669', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: '6px' }} onClick={() => handleEndLunch(todayLunch)} disabled={saving}>
          🍽️ End Lunch
        </button>
      )
    }
    return (
      <button className="btn btn-secondary" style={{ background: '#f1f5f9', color: '#64748b', borderColor: '#cbd5e1', padding: '6px 12px', cursor: 'not-allowed', display: 'flex', alignItems: 'center', gap: '6px' }} disabled>
        ✅ Lunch Done
      </button>
    )
  }

  // 2. Start Visit: Ride start (planned) OR school-time start (arrived)
  const handleStartVisit = async (visitId, phase = 'ride', pickedLocation = null) => {
    if (phase === 'ride') {
      const activeVisits = visits.filter(v => v.status === 'in_progress' || v.status === 'at_school' || v.status === 'riding_home')
      if (activeVisits.length > 0) {
        const blockingNames = activeVisits.map(v => v.area_name).join(', ')
        Swal.fire({ 
          icon: 'warning', 
          title: 'Cannot Start Ride', 
          text: `You have active ongoing tasks (${blockingNames}). Please finish them before starting a new ride.` 
        })
        return
      }
    }

    // School start — photo is optional; start immediately
    if (phase === 'school') {
      const visit = visits.find(v => v.id === visitId)
      if (!visit) return
      await completeSchoolStart(visit)
      return
    }

    const startedVisitBase = visits.find(v => v.id === visitId)
    const isSupportRide = phase === 'ride' && (
      startedVisitBase?.is_support_visit || String(visitId).startsWith('support-')
    )
    const hasSavedLocation = (row) => {
      const lat = parseFloat(row?.latitude)
      const lng = parseFloat(row?.longitude)
      return Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0)
    }
    if (isSupportRide && !pickedLocation && startedVisitBase && (startedVisitBase.needs_location || !hasSavedLocation(startedVisitBase))) {
      const here = locationRef.current
      setLocationPick({
        visit: startedVisitBase,
        latitude: here?.latitude ? Number(here.latitude).toFixed(7) : '12.9716000',
        longitude: here?.longitude ? Number(here.longitude).toFixed(7) : '77.5946000',
      })
      return
    }

    if (!navigator.geolocation) {
      Swal.fire({ icon: 'warning', title: 'GPS Not Supported', text: 'Your device or browser does not support GPS.' })
      return
    }

    Swal.fire({ title: 'Acquiring GPS...', text: 'Please wait.', allowOutsideClick: false, didOpen: () => Swal.showLoading() })
    let lat, lng, acc = 100;

    // Use instantly cached background location if available
    if (locationRef.current && locationRef.current.latitude) {
      lat = parseFloat(locationRef.current.latitude).toFixed(7)
      lng = parseFloat(locationRef.current.longitude).toFixed(7)
      Swal.close()
    } else {
      try {
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 6000, maximumAge: 0 })
        })
        lat = position.coords.latitude.toFixed(7)
        lng = position.coords.longitude.toFixed(7)
        acc = position.coords.accuracy
        Swal.close()
      } catch (err) {
        Swal.close()
        Swal.fire({ icon: 'error', title: 'GPS Required', text: 'Failed to get GPS location. Please ensure Location is turned on.' })
        return
      }
    }

    const payload = { latitude: lat, longitude: lng, accuracy: acc }

    let resolvedId = visitId
    const startedVisit = pickedLocation
      ? { ...startedVisitBase, latitude: pickedLocation.latitude, longitude: pickedLocation.longitude, needs_location: false }
      : startedVisitBase
    if (String(visitId).startsWith('support-')) {
      try {
        const ensured = await api.post('/visits/ensure-support-visit/', {
          company_visit_id: String(visitId).replace('support-', ''),
        })
        resolvedId = ensured.data?.id
        if (!resolvedId) throw new Error('Could not open this support visit for a ride.')
      } catch (err) {
        Swal.fire({ icon: 'error', title: 'Failed', text: err.response?.data?.error || err.message || 'Could not start this support visit.' })
        return
      }
    }

    if (startedVisit) saveSchoolDestination({ ...startedVisit, id: resolvedId })

    localStorage.setItem(`visit_start_gps_${resolvedId}`, JSON.stringify({ lat, lng }))
    localStorage.setItem(`visit_last_gps_${resolvedId}`, JSON.stringify({ lat, lng }))
    localStorage.setItem(`visit_km_${resolvedId}`, '0.0')
    setLiveDistances(prev => ({ ...prev, [resolvedId]: '0.0' }))

    try {
      await api.post(`/visits/${resolvedId}/start_visit/`, payload)
      fetchVisits()
      if (startedVisit) {
        openRouteGuide({ ...startedVisit, id: resolvedId }, { lat, lng })
      }
    } catch (err) {
      alert(err.response?.data?.error || err.response?.data?.detail || 'Failed to start visit.')
    }
  }

  const confirmSupportLocation = async () => {
    if (!locationPick?.visit) return
    const { visit, latitude, longitude } = locationPick
    const rawId = String(visit.id)
    const companyVisitId = visit.company_visit_id
      || (rawId.startsWith('support-') ? rawId.replace('support-', '') : '')
    setSavingLocation(true)
    let address = (locationPick.address || '').trim()
    if (!address) {
      try {
        const body = await reverseGeocode(latitude, longitude)
        address = body.address || ''
      } catch { /* coordinates are still saved */ }
    }
    try {
      await api.post('/visits/save-support-location/', {
        ...(companyVisitId ? { company_visit_id: companyVisitId } : { visit_id: visit.id }),
        latitude,
        longitude,
        address,
      })
      setLocationPick(null)
      setSavingLocation(false)
      handleStartVisit(visit.id, 'ride', { latitude, longitude })
    } catch (err) {
      setSavingLocation(false)
      Swal.fire({
        icon: 'error',
        title: 'Could not save location',
        text: err.response?.data?.error || 'The picked location was not saved.',
      })
    }
  }

  const completeSchoolStart = async (visit) => {
    if (!navigator.geolocation && !(locationRef.current && locationRef.current.latitude)) {
      Swal.fire({ icon: 'warning', title: 'GPS Not Supported', text: 'Your device or browser does not support GPS.' })
      return false
    }

    Swal.fire({ title: 'Starting school time...', text: 'Please wait.', allowOutsideClick: false, didOpen: () => Swal.showLoading() })
    let lat, lng, acc = 100

    if (locationRef.current && locationRef.current.latitude) {
      lat = parseFloat(locationRef.current.latitude).toFixed(7)
      lng = parseFloat(locationRef.current.longitude).toFixed(7)
    } else {
      try {
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 6000, maximumAge: 0 })
        })
        lat = position.coords.latitude.toFixed(7)
        lng = position.coords.longitude.toFixed(7)
        acc = position.coords.accuracy
      } catch (err) {
        Swal.close()
        Swal.fire({ icon: 'error', title: 'GPS Required', text: 'Failed to get GPS location. Please ensure Location is turned on.' })
        return false
      }
    }

    try {
      await api.post(`/visits/${visit.id}/start_visit/`, { latitude: lat, longitude: lng, accuracy: acc })
      fetchVisits()
      Swal.fire({
        icon: 'success',
        title: 'School Time Started!',
        text: 'Timer is running for time spent in school.',
        timer: 2000,
        showConfirmButton: false
      })
      return true
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Failed', text: err.response?.data?.error || 'Failed to start school visit.' })
      return false
    }
  }

  // 3a. End Ride: optional photo → save distance → auto-start school time
  const buildGpsPayload = async () => {
    const payload = {}
    if (locationRef.current && locationRef.current.latitude) {
      payload.latitude = parseFloat(locationRef.current.latitude).toFixed(7)
      payload.longitude = parseFloat(locationRef.current.longitude).toFixed(7)
      payload.accuracy = 100
      return payload
    }
    if (!navigator.geolocation) return payload
    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true, timeout: 6000, maximumAge: 0
        })
      })
      payload.latitude = position.coords.latitude.toFixed(7)
      payload.longitude = position.coords.longitude.toFixed(7)
      payload.accuracy = position.coords.accuracy
    } catch (err) {
      console.warn('Could not get GPS:', err)
    }
    return payload
  }

  const askTransportDetails = async (title) => {
    const { value: transportDetails } = await Swal.fire({
      title,
      html: `
        <div style="text-align: left; margin-bottom: 15px;">
          <label style="display: block; font-size: 13px; font-weight: 600; margin-bottom: 5px;">Transport Mode</label>
          <select id="swal-transport-mode" class="swal2-select" style="width: 100%; font-size: 14px; padding: 5px; margin: 0; box-sizing: border-box;" onchange="
            const chargeDiv = document.getElementById('swal-bus-charge-div');
            if(this.value === 'bus') {
              chargeDiv.style.display = 'block';
            } else {
              chargeDiv.style.display = 'none';
            }
          ">
            <option value="bike">🏍️ Bike</option>
            <option value="bus">🚌 Bus</option>
          </select>
        </div>
        <div id="swal-bus-charge-div" style="display: none; text-align: left;">
          <label style="display: block; font-size: 13px; font-weight: 600; margin-bottom: 5px;">Bus Charge Amount (₹)</label>
          <input id="swal-bus-charge" type="number" class="swal2-input" style="width: 100%; margin: 0; padding: 5px; box-sizing: border-box;" placeholder="Enter amount..." />
        </div>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Continue',
      cancelButtonText: 'Cancel',
      preConfirm: () => {
        const mode = document.getElementById('swal-transport-mode').value
        const charge = document.getElementById('swal-bus-charge').value
        if (mode === 'bus' && !charge) {
          Swal.showValidationMessage('Please enter the bus Charge amount')
          return false
        }
        return { transport_mode: mode, bus_charge: mode === 'bus' ? parseFloat(charge) : 0 }
      }
    })
    return transportDetails || null
  }

  const finalizeEndRideAndStartSchool = async (visit, basePayload) => {
    const visitId = visit.id
    const gps = await buildGpsPayload()
    const payload = {
      ...basePayload,
      ...gps,
      auto_start_school: true,
    }

    try {
      Swal.fire({ title: 'Ending ride & starting school...', allowOutsideClick: false, didOpen: () => Swal.showLoading() })
      await api.post(`/visits/${visitId}/end_visit/`, payload)
      localStorage.removeItem(`visit_km_${visitId}`)
      localStorage.removeItem(`visit_last_gps_${visitId}`)
      setLiveDistances(prev => {
        const next = { ...prev }
        delete next[visitId]
        return next
      })
      if (activeRoute?.visitId === visitId) {
        setRoutePanelOpen(false)
        setRouteFullscreen(false)
        setActiveRoute(null)
      }
      fetchVisits()
      await Swal.fire({
        icon: 'success',
        title: 'School Time Started!',
        text: `Ride: ${parseFloat(basePayload.distance_km || 0).toFixed(1)} km. School timer is now running.`,
        timer: 2200,
        showConfirmButton: false
      })
      return true
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Failed', text: err.response?.data?.error || 'Failed to end ride.' })
      return false
    }
  }

  const handleEndRide = async (visit) => {
    const visitId = visit.id
    const finalDistance = parseFloat(liveDistances[visitId] || localStorage.getItem(`visit_km_${visitId}`) || 0)
    const transportDetails = await askTransportDetails('Reached School')
    if (!transportDetails) return

    const basePayload = {
      distance_km: finalDistance,
      transport_mode: transportDetails.transport_mode,
      bus_charge: transportDetails.bus_charge
    }

    // Optional arrival photo
    const photoChoice = await Swal.fire({
      title: 'Arrival photo?',
      text: 'Photo is optional. You can skip and continue.',
      icon: 'question',
      showCancelButton: true,
      showDenyButton: true,
      confirmButtonText: 'Take Photo',
      denyButtonText: 'Skip Photo',
      cancelButtonText: 'Cancel',
    })

    if (photoChoice.isDismissed) return

    if (photoChoice.isConfirmed) {
      pendingRideEndRef.current = { visit, payload: basePayload }
      await openCameraForVisit(visit, 'ride_end')
      return
    }

    await finalizeEndRideAndStartSchool(visit, basePayload)
  }

  const beginRideHome = async (visit) => {
    const blocking = visits.filter((item) => (
      !isHomeCommute(item) && (item.status === 'in_progress' || item.status === 'riding_home' || item.status === 'at_school')
    ))
    if (blocking.length > 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Finish the current visit',
        text: blocking.map((item) => item.area_name).join(', '),
      })
      return
    }
    let target = visit
    if (visit.status !== 'visited') {
      try {
        await api.patch(`/visits/${visit.id}/`, { status: 'visited' })
        target = { ...visit, status: 'visited' }
      } catch (err) {
        Swal.fire({ icon: 'error', title: 'Failed', text: err.response?.data?.error || 'Could not start the ride home.' })
        return
      }
    }
    handleStartRideHome(target)
  }

  const handleStartRideHome = async (visit) => {
    if (!navigator.geolocation && !(locationRef.current && locationRef.current.latitude)) {
      Swal.fire({ icon: 'warning', title: 'GPS Not Supported', text: 'Your device or browser does not support GPS.' })
      return
    }

    Swal.fire({ title: 'Starting ride home...', text: 'Please wait.', allowOutsideClick: false, didOpen: () => Swal.showLoading() })
    let lat, lng, acc = 100

    if (locationRef.current && locationRef.current.latitude) {
      lat = parseFloat(locationRef.current.latitude).toFixed(7)
      lng = parseFloat(locationRef.current.longitude).toFixed(7)
    } else {
      try {
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 6000, maximumAge: 0 })
        })
        lat = position.coords.latitude.toFixed(7)
        lng = position.coords.longitude.toFixed(7)
        acc = position.coords.accuracy
      } catch (err) {
        Swal.close()
        Swal.fire({ icon: 'error', title: 'GPS Required', text: 'Failed to get GPS location.' })
        return
      }
    }

    localStorage.setItem(`visit_home_last_gps_${visit.id}`, JSON.stringify({ lat: parseFloat(lat), lng: parseFloat(lng) }))
    localStorage.setItem(`visit_home_km_${visit.id}`, '0.0')
    setLiveDistances(prev => ({ ...prev, [visit.id]: '0.0' }))

    try {
      const started = await api.post(`/visits/${visit.id}/start_visit/`, { latitude: lat, longitude: lng, accuracy: acc })
      if (isHomeCommute(visit)) {
        rememberHomeVisit(started.data?.id ? started.data : {
          ...visit,
          status: 'riding_home',
          home_start_time: new Date().toISOString(),
          home_end_time: null,
        })
      }
      fetchVisits()
      Swal.fire({
        icon: 'success',
        title: 'Ride home started',
        text: 'Tap Reached home when you arrive.',
        timer: 2200,
        showConfirmButton: false
      })
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Failed', text: err.response?.data?.error || 'Failed to start ride home.' })
    }
  }

  const handleEndRideHome = async (visit) => {
    const visitId = visit.id
    const finalDistance = parseFloat(liveDistances[visitId] || localStorage.getItem(`visit_home_km_${visitId}`) || 0)
    const transportDetails = await askTransportDetails('Reached Home')
    if (!transportDetails) return

    const gps = await buildGpsPayload()
    const payload = {
      distance_km: finalDistance,
      transport_mode: transportDetails.transport_mode,
      bus_charge: transportDetails.bus_charge,
      ...gps,
    }

    try {
      Swal.fire({ title: 'Ending ride home...', allowOutsideClick: false, didOpen: () => Swal.showLoading() })
      const ended = await api.post(`/visits/${visitId}/end_visit/`, payload)
      if (isHomeCommute(visit)) {
        rememberHomeVisit(ended.data?.id ? ended.data : {
          ...visit,
          status: 'visited',
          home_end_time: new Date().toISOString(),
          home_distance_km: finalDistance,
        })
      }
      localStorage.removeItem(`visit_home_km_${visitId}`)
      localStorage.removeItem(`visit_home_last_gps_${visitId}`)
      setLiveDistances(prev => {
        const next = { ...prev }
        delete next[visitId]
        return next
      })
      fetchVisits()
      await Swal.fire({
        icon: 'success',
        title: 'Reached home',
        text: `Location to home: ${finalDistance.toFixed(1)} km saved to expenses.`,
        timer: 2200,
        showConfirmButton: false
      })
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Failed', text: err.response?.data?.error || 'Failed to end ride home.' })
    }
  }

  const loadSupportChecklist = async (companyId) => {
    if (!companyId) return []
    const res = await api.get('/company/company-checklists/', { params: { company_id: companyId } })
    const rows = Array.isArray(res.data) ? res.data : (res.data?.data || [])
    return [...rows].sort((a, b) => a.id - b.id)
  }

  const openSupportEndForm = async (visit) => {
    if (!visit) return
    setSupportEnd({ visit, loading: true, saving: false, companyId: null, companyName: schoolName(visit.area_name), items: [], feedback: '' })
    try {
      const ctx = await api.get(`/visits/${visit.id}/support-context/`)
      const companyId = ctx.data?.company_id || null
      const items = await loadSupportChecklist(companyId)
      setSupportEnd(prev => prev && ({
        ...prev,
        loading: false,
        companyId,
        companyName: ctx.data?.company_name || prev.companyName,
        items,
        feedback: ctx.data?.client_feedback || '',
      }))
    } catch (err) {
      console.error('Failed to load support visit checklist:', err?.response?.data || err)
      setSupportEnd(prev => prev && ({ ...prev, loading: false }))
    }
  }

  const openPostVisitForm = async (visit) => {
    if (isSupportVisit(visit)) await openSupportEndForm(visit)
    else await openVisitDetailsForm(visit)
  }

  const assignAllSupportChecklist = async () => {
    const companyId = supportEnd?.companyId
    if (!companyId) return
    setSupportEnd(prev => prev && ({ ...prev, loading: true }))
    try {
      await api.post('/company/company-checklists/assign-all/', { company_id: companyId })
      const items = await loadSupportChecklist(companyId)
      setSupportEnd(prev => prev && ({ ...prev, loading: false, items }))
    } catch (err) {
      setSupportEnd(prev => prev && ({ ...prev, loading: false }))
      Swal.fire({ icon: 'error', title: 'Failed', text: 'Could not assign checklist items.' })
    }
  }

  const toggleSupportChecklistItem = async (item, isDone) => {
    setSupportEnd(prev => prev && ({
      ...prev,
      items: prev.items.map(ci => (ci.id === item.id ? { ...ci, is_done: isDone } : ci)),
    }))
    try {
      await api.patch(`/company/company-checklists/${item.id}/`, { is_done: isDone })
    } catch (err) {
      setSupportEnd(prev => prev && ({
        ...prev,
        items: prev.items.map(ci => (ci.id === item.id ? { ...ci, is_done: item.is_done } : ci)),
      }))
      Swal.fire({ icon: 'error', title: 'Failed', text: 'Could not update the checklist item.' })
    }
  }

  const saveSupportEndForm = async () => {
    if (!supportEnd?.visit) return
    const feedback = (supportEnd.feedback || '').trim()
    if (!feedback) {
      Swal.fire({ icon: 'warning', title: 'Client feedback required', text: 'Enter what the client said about the visit.' })
      return
    }
    setSupportEnd(prev => prev && ({ ...prev, saving: true }))
    try {
      await api.post(`/visits/${supportEnd.visit.id}/support-feedback/`, { client_feedback: feedback })
      setSupportEnd(null)
      fetchVisits()
      Swal.fire({ icon: 'success', title: 'Saved', text: 'Checklist and client feedback saved', timer: 2000, showConfirmButton: false })
    } catch (err) {
      setSupportEnd(prev => prev && ({ ...prev, saving: false }))
      Swal.fire({ icon: 'error', title: 'Failed', text: err.response?.data?.error || 'Could not save client feedback.' })
    }
  }

  // 3b. End School Visit — photo is optional
  const handleEndSchoolVisit = async (visit) => {
    const ok = await completeSchoolEnd(visit)
    if (ok) await openPostVisitForm(visit)
  }

  const completeSchoolEnd = async (visit) => {
    const visitId = visit.id
    let payload = await buildGpsPayload()

    try {
      Swal.fire({ title: 'Ending school visit...', allowOutsideClick: false, didOpen: () => Swal.showLoading() })
      await api.post(`/visits/${visitId}/end_visit/`, payload)
      Swal.close()
      fetchVisits()
      return true
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Failed', text: err.response?.data?.error || 'Failed to end school visit.' })
      return false
    }
  }

  // Back-compat alias used by any remaining callers
  const handleEndVisit = handleEndRide


  const handleDeleteVisit = async (visitId) => {
    if (!window.confirm('Delete this visit and all its media?')) return
    try {
      await api.delete(`/visits/${visitId}/`)
      fetchVisits()
    } catch { alert('Failed to delete visit.') }
  }

  const openMediaModal = async (visit) => {
    setSelectedVisit(visit)
    setShowMediaModal(true)
    try {
      const res = await api.get('/media/', { params: { visit_id: visit.id } })
      console.log("Fetched media list:", res.data)
      setMedia(res.data.results || res.data)
    } catch (err) {
      console.error("Failed to load media list:", err)
      setMedia([])
    }
  }

  const handleDeleteMedia = async (mediaId) => {
    if (!window.confirm('Delete this media?')) return
    try {
      await api.delete(`/media/${mediaId}/`)
      setMedia(m => m.filter(item => item.id !== mediaId))
      fetchVisits()
    } catch { alert('Failed to delete media.') }
  }

  const getMediaUrl = (item) => {
    let url = item.file_url || item.file
    if (!url) return ''
    if (url.includes('amazonaws.com')){
      return url
    }

    const host = window.location.hostname
    const protocol = window.location.protocol

    if (url.startsWith('http://') || url.startsWith('https://')) {
      try {
        const parsedUrl = new URL(url)
        if (parsedUrl.hostname === 'localhost' || parsedUrl.hostname === '127.0.0.1') {
          parsedUrl.protocol = protocol
          parsedUrl.hostname = host
          parsedUrl.port = '8000'
        }
        return parsedUrl.toString()
      } catch (e) {
        return url
      }
    }
    const prefix = url.startsWith('/') ? '' : '/'
    return `${protocol}//${host}:8000${prefix}${url}`
  }

  const downloadImage = async (url, filename) => {
    try {
      const response = await fetch(url)
      const blob = await response.blob()
      const blobUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = blobUrl
      a.download = filename || 'download.jpg'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(blobUrl)
    } catch (err) {
      console.error("Failed to download image:", err)
      // Fallback: open in new tab
      window.open(url, '_blank')
    }
  }

  const initCaptureForm = (visit) => ({
    interest_status: (visit.interest_status && visit.interest_status !== 'pending')
      ? visit.interest_status
      : '',
    contact_number: visit.contact_number || '',
    strength: visit.strength || '',
    interested_services: visit.interested_services
      ? (typeof visit.interested_services === 'string'
        ? visit.interested_services.split(',').map(s => s.trim()).filter(Boolean)
        : visit.interested_services)
      : [],
    other_interests: visit.other_interests || '',
    not_interested_reason: visit.not_interested_reason || '',
    main_branch_search_query: visit.main_branch_search_query || '',
    next_visit_plan_date: visit.next_visit_plan_date
      ? String(visit.next_visit_plan_date).substring(0, 10)
      : new Date().toLocaleDateString('en-CA'),
    followup_date: defaultFollowupDate(),
    wants_parents_orientation: visit.wants_parents_orientation || false,
    wants_student_orientation: visit.wants_student_orientation || false,
    wants_staff_orientation: visit.wants_staff_orientation || false,
    wants_revisit: visit.wants_revisit || false,
  })

  const openVisitDetailsForm = async (visit) => {
    if (!visit) return
    capturingVisitRef.current = visit
    setCameraPurpose('media')
    setPhotoOnly(false)
    setDetailsOnly(true)
    setCapturedPhoto(null)
    setCaptureForm(initCaptureForm(visit))
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          locationRef.current = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          }
        },
        (error) => console.error("Error refreshing location:", error),
        { enableHighAccuracy: true, timeout: 5000 }
      )
    }
    setShowCamera(true)
  }

  const openCameraForVisit = async (visit, purpose = 'media') => {
    if (!visit) return

    // Verify secure context for Geolocation / MediaDevices access
    if (!window.isSecureContext && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
      Swal.fire({
        icon: 'warning',
        title: 'Secure Connection Required',
        text: 'Camera capture and GPS tracking require a secure HTTPS connection. Please access this app via HTTPS.'
      })
      return
    }

    if (!navigator.geolocation) {
      Swal.fire({
        icon: 'error',
        title: 'GPS Not Supported',
        text: 'Your browser or device does not support GPS location tracking.'
      })
      return
    }

    capturingVisitRef.current = visit
    setCameraPurpose(purpose)
    setPhotoOnly(false)
    setDetailsOnly(false)
    setCapturedPhoto(null)
    setCaptureForm(initCaptureForm(visit))

    // Trigger location update in background
    navigator.geolocation.getCurrentPosition(
      (position) => {
        locationRef.current = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        }
      },
      (error) => console.error("Error refreshing location:", error),
      { enableHighAccuracy: true, timeout: 5000 }
    )

    setShowCamera(true)
  }

  const startCaptureAndUpload = async (visit) => {
    await openCameraForVisit(visit, 'media')
    setPhotoOnly(true)
  }

  const continueWithoutPhoto = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop())
      streamRef.current = null
    }
    setCapturedPhoto(null)
    setPhotoOnly(false)
    setDetailsOnly(true)
  }

  const handleCancelCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop())
      streamRef.current = null
    }
    setCapturedPhoto(null)
    setDetailsOnly(false)
    setPhotoOnly(false)
    setShowCamera(false)
    setCameraPurpose('media')
    capturingVisitRef.current = null
    pendingRideEndRef.current = null
  }

  const closeCameraStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop())
      streamRef.current = null
    }
    setCapturedPhoto(null)
    setDetailsOnly(false)
    setPhotoOnly(false)
    setShowCamera(false)
  }

  const sendReachedSchoolWhatsApp = async (photo) => {
    const visit = capturingVisitRef.current
    const when = photo?.takenAt ? new Date(photo.takenAt) : new Date()
    const lat = photo?.latitude ?? locationRef.current?.latitude ?? visit?.latitude
    const lng = photo?.longitude ?? locationRef.current?.longitude ?? visit?.longitude
    const text = reachedSchoolMessage({
      school: schoolName(visit?.area_name),
      lat,
      lng,
      when,
    })
    const file = photo?.blob
      ? new File([photo.blob], `school_reached_${Date.now()}.jpg`, { type: 'image/jpeg' })
      : null
    await shareOnWhatsApp({ file, text })
  }

  const takePhoto = () => {
    const video = videoRef.current
    if (!video) return

    // Create a canvas to draw the frame
    const canvas = document.createElement('canvas')
    const MAX_WIDTH = 800;
    const scale = Math.min(MAX_WIDTH / (video.videoWidth || 640), 1);
    canvas.width = (video.videoWidth || 640) * scale;
    canvas.height = (video.videoHeight || 480) * scale;
    const ctx = canvas.getContext('2d')
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height)

    const visit = capturingVisitRef.current
    const when = new Date()
    const lat = locationRef.current?.latitude ?? visit?.latitude
    const lng = locationRef.current?.longitude ?? visit?.longitude
    const isReachedSchool = cameraPurpose === 'ride_end'
    if (isReachedSchool) {
      const caption = [
        schoolName(visit?.area_name) || 'Reached school',
        (lat != null && lng != null) ? `${Number(lat).toFixed(6)}, ${Number(lng).toFixed(6)}` : '',
        when.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }),
      ].filter(Boolean)
      stampPhotoCaption(ctx, canvas.width, canvas.height, caption)
    }

    const dataUrl = canvas.toDataURL('image/jpeg', 0.6)
    const blob = dataUrlToBlob(dataUrl)
    const photo = {
      dataUrl,
      blob,
      takenAt: when.toISOString(),
      latitude: lat,
      longitude: lng,
    }
    setCapturedPhoto(photo)
    if (isReachedSchool) {
      sendReachedSchoolWhatsApp(photo)
    }

    // Pre-fetch location in the background
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          locationRef.current = { latitude: pos.coords.latitude, longitude: pos.coords.longitude }
        },
        (err) => console.warn("Background location fetch failed:", err),
        { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
      )
    }
  }

  const discardPhoto = () => {
    setCapturedPhoto(null)
  }

  const saveAndUploadPhoto = async () => {
    const visit = capturingVisitRef.current
    if (!visit) return

    const purpose = cameraPurpose
    const isCheckpoint = purpose === 'school_start' || purpose === 'school_end' || purpose === 'ride_end'
    const hasPhoto = Boolean(capturedPhoto?.blob)
    const isPhotoOnly = photoOnly && purpose === 'media'

    if (isPhotoOnly && !hasPhoto) return

    // Checkpoint without photo — continue with the action
    if (isCheckpoint && !hasPhoto) {
      closeCameraStream()
      setCameraPurpose('media')
      if (purpose === 'school_start') {
        await completeSchoolStart(visit)
        capturingVisitRef.current = null
      } else if (purpose === 'ride_end') {
        const pending = pendingRideEndRef.current
        pendingRideEndRef.current = null
        if (pending) await finalizeEndRideAndStartSchool(pending.visit, pending.payload)
        capturingVisitRef.current = null
      } else {
        const ok = await completeSchoolEnd(visit)
        if (ok) await openPostVisitForm(visit)
      }
      return
    }

    // Show loading spinner
    Swal.fire({
      title: hasPhoto ? 'Uploading...' : 'Saving...',
      text: hasPhoto
        ? (isCheckpoint ? 'Saving checkpoint photo...' : 'Uploading captured photo with location...')
        : 'Saving visit details...',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading()
      }
    })

    // Create file object from blob when photo was taken
    const file = hasPhoto
      ? new File(
          [capturedPhoto.blob],
          `${purpose === 'school_start' ? 'school_start' : purpose === 'school_end' ? 'school_end' : purpose === 'ride_end' ? 'ride_end' : 'visit_photo'}_${Date.now()}.jpg`,
          { type: 'image/jpeg' }
        )
      : null

    const uploadWithCoords = async (lat, lng) => {
      try {
        if (hasPhoto && file) {
          const formData = new FormData()
          formData.append('visit', visit.id)
          formData.append('file', file)
          formData.append(
            'caption',
            purpose === 'school_start'
              ? 'School start photo'
              : purpose === 'school_end'
                ? 'School end photo'
                : purpose === 'ride_end'
                  ? 'Ride end / arrival photo'
                  : ''
          )
          formData.append('media_type', 'photo')
          if (lat !== undefined && lat !== null) {
            formData.append('latitude', parseFloat(lat).toFixed(7))
          }
          if (lng !== undefined && lng !== null) {
            formData.append('longitude', parseFloat(lng).toFixed(7))
          }

          await api.post('/media/', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          })
        }

        if (isPhotoOnly) {
          Swal.fire({
            icon: 'success',
            title: 'Saved',
            text: 'Image saved successfully',
            timer: 2000,
            showConfirmButton: false
          })
          closeCameraStream()
          setCameraPurpose('media')
          capturingVisitRef.current = null
          openMediaModal(visit)
          return
        }

        if (isCheckpoint) {
          closeCameraStream()
          setCameraPurpose('media')

          if (purpose === 'school_start') {
            const ok = await completeSchoolStart(visit)
            if (!ok) return
            capturingVisitRef.current = null
            return
          }

          if (purpose === 'ride_end') {
            const pending = pendingRideEndRef.current
            pendingRideEndRef.current = null
            if (pending) await finalizeEndRideAndStartSchool(pending.visit, pending.payload)
            capturingVisitRef.current = null
            return
          }

          if (purpose === 'school_end') {
            const ok = await completeSchoolEnd(visit)
            if (!ok) return
            // After end, collect interest / visit details (photo optional)
            await openPostVisitForm(visit)
            return
          }
        }

        // Full media / details capture: save client details + GPS coords on the visit
        const patchPayload = {
          interest_status: captureForm.interest_status,
          status: 'visited',
          strength: captureForm.strength || null,
          wants_parents_orientation: captureForm.wants_parents_orientation,
          wants_student_orientation: captureForm.wants_student_orientation,
          wants_staff_orientation: captureForm.wants_staff_orientation,
          wants_revisit: captureForm.wants_revisit,
          contact_number: captureForm.contact_number,
          interested_services: Array.isArray(captureForm.interested_services)
            ? captureForm.interested_services.join(', ')
            : (captureForm.interested_services || ''),
          other_interests: captureForm.other_interests,
          not_interested_reason: captureForm.not_interested_reason,
          main_branch_search_query: captureForm.main_branch_search_query,
          next_visit_plan_date: captureForm.next_visit_plan_date || null,
        }
        if (lat !== undefined && lat !== null) {
          patchPayload.latitude = parseFloat(lat).toFixed(7)
        }
        if (lng !== undefined && lng !== null) {
          patchPayload.longitude = parseFloat(lng).toFixed(7)
        }

        await api.patch(`/visits/${visit.id}/`, patchPayload)

        if (captureForm.interest_status === 'followup' || captureForm.interest_status === 'interested') {
          try {
            await api.post('/visits/followups/', {
              visit: visit.id,
              academic_year: selectedAcademicYear || visit.academic_year,
              followup_date: captureForm.followup_date || null,
              next_purpose: captureForm.followup_purpose?.trim() || 'Interested prospect follow-up',
              notes: hasPhoto ? 'Created from media capture' : 'Created from visit details'
            })
            if (filterStatus === 'followups') fetchFollowups()
          } catch (err) {
            console.error("Failed to auto-create follow-up:", err?.response?.data || err)
          }
        }

        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: hasPhoto ? 'Media saved successfully' : 'Visit details saved successfully',
          timer: 2000,
          showConfirmButton: false
        })

        closeCameraStream()
        setCameraPurpose('media')
        capturingVisitRef.current = null
        fetchVisits()
        if (hasPhoto) openMediaModal(visit)
      } catch (err) {
        console.error("Failed to upload media:", err)
        const data = err.response?.data
        let errorMsg = err.message || 'Upload failed'
        if (data) {
          if (typeof data === 'object') {
            errorMsg = data.detail || data.error || JSON.stringify(data)
          } else if (typeof data === 'string') {
            const match = data.match(/Exception Value:<\/th>\s*<td><pre>([\s\S]*?)<\/pre>/i)
              || data.match(/pre class="exception_value">([\s\S]*?)<\/pre>/i)
            errorMsg = match ? match[1].replace(/<[^>]+>/g, '').trim() : 'Server error while uploading to AWS S3'
          }
        }
        Swal.fire({
          icon: 'error',
          title: 'Upload Failed',
          text: errorMsg.length > 280 ? `${errorMsg.slice(0, 280)}…` : errorMsg
        })
      }
    }

    const checkLocationAndUpload = async () => {
      if (locationRef.current && locationRef.current.latitude) {
        await uploadWithCoords(locationRef.current.latitude, locationRef.current.longitude)
        return
      }

      if (navigator.geolocation) {
        Swal.fire({
          title: 'Acquiring GPS Location...',
          text: 'Fetching your live coordinates.',
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading()
          }
        })

        navigator.geolocation.getCurrentPosition(
          async (pos) => {
            Swal.close()
            await uploadWithCoords(pos.coords.latitude, pos.coords.longitude)
          },
          async (err) => {
            Swal.close()
            console.warn("Failed to get live location, trying cached location if available:", err)
            if (locationRef.current) {
              await uploadWithCoords(locationRef.current.latitude, locationRef.current.longitude)
            } else if (visit.latitude && visit.longitude) {
              await uploadWithCoords(visit.latitude, visit.longitude)
            } else if (!hasPhoto) {
              // Details without photo — GPS optional
              await uploadWithCoords(null, null)
            } else {
              Swal.fire({
                icon: 'error',
                title: 'Location Required',
                text: 'Could not acquire GPS location. Please ensure location services/GPS are enabled on your device, location permission is granted to this site, and try again.'
              })
            }
          },
          {
            enableHighAccuracy: true,
            timeout: 1000,
            maximumAge: 0
          }
        )
      } else {
        if (visit.latitude && visit.longitude) {
          await uploadWithCoords(visit.latitude, visit.longitude)
        } else if (!hasPhoto) {
          await uploadWithCoords(null, null)
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Not Supported',
            text: 'Geolocation is not supported by your browser.'
          })
        }
      }
    }

    checkLocationAndUpload()
  }

  // Hook to handle stream creation/cleanup based on showCamera state
  useEffect(() => {
    let activeStream = null
    let watchId = null

    const startStream = async () => {
      if (showCamera) {
        // Start watching position continuously while camera/details modal is open
        if (navigator.geolocation) {
          watchId = navigator.geolocation.watchPosition(
            (pos) => {
              locationRef.current = { latitude: pos.coords.latitude, longitude: pos.coords.longitude }
            },
            (err) => console.warn("Live location watch failed:", err),
            { enableHighAccuracy: true, maximumAge: 0, timeout: 5000 }
          )
        }

        // Details-only mode does not need camera
        if (detailsOnly) return

        try {
          const constraints = {
            video: {
              facingMode: { ideal: 'environment' },
              width: { ideal: 1280 },
              height: { ideal: 720 }
            },
            audio: false
          }
          const stream = await navigator.mediaDevices.getUserMedia(constraints)
          activeStream = stream
          streamRef.current = stream
          if (videoRef.current) {
            videoRef.current.srcObject = stream
          }
        } catch (err) {
          console.error("Failed to acquire camera stream:", err)
          Swal.fire({
            icon: 'error',
            title: 'Camera Error',
            text: 'Could not access camera. You can continue without a photo.',
            showCancelButton: true,
            confirmButtonText: 'Continue without photo',
            cancelButtonText: 'Cancel'
          }).then((result) => {
            if (result.isConfirmed) {
              setDetailsOnly(true)
            } else {
              setShowCamera(false)
            }
          })
        }
      }
    }

    startStream()

    return () => {
      if (activeStream) {
        activeStream.getTracks().forEach(track => track.stop())
      }
      if (watchId !== null && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchId)
      }
    }
  }, [showCamera, detailsOnly])

  const statusBadge = (status) => (
    <span className={`badge badge-${status}`}>
      {status === 'in_progress' ? 'Riding' :
        status === 'arrived' ? 'Arrived' :
          status === 'at_school' ? 'In School' :
            status === 'riding_home' ? 'Riding Home' :
              status === 'visited' ? 'Visited' : 'Planned'}
    </span>
  )

  const interestBadge = (interest) => {
    if (!interest || interest === 'pending') return <span style={{ color: 'var(--text-muted)' }}>—</span>
    const label = String(interest).replace(/_/g, ' ')
    const chip = interest === 'interested'
      ? 'chip-green'
      : interest === 'not_interested'
        ? 'chip-orange'
        : 'chip-slate'
    return <span className={`chip ${chip}`} style={{ textTransform: 'capitalize' }}>{label}</span>
  }

  const openVisitInMaps = (visit) => {
    const point = visitPoint(visit)
    const url = mapsQueryUrl(point?.lat, point?.lng, visit.area_name || schoolName(visit.area_name))
    if (!url) return
    window.open(url, '_blank', 'noopener')
  }

  const openQuotationPage = (visit) => {
    navigate('/quotation/add', {
      state: {
        visit: {
          visitId: visit.id,
          schoolName: schoolName(visit.area_name),
          schoolAddress: visit.area_name || '',
          contactPhone: visit.contact_number || '',
          notes: visit.interested_services || '',
        },
      },
    })
  }

  const renderVisitActions = (visit) => (
    <div className="visit-actions">
      {myVisitsOnly && (
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          title="Open this school in Google Maps"
          onClick={() => openVisitInMaps(visit)}
        >
          <MapPin size={12} /> Open in map
        </button>
      )}
      {myVisitsOnly && visit.interest_status === 'interested' && (
        <>
          {visit.quotation_status === 'generated' && <span className="chip chip-slate">Quotation generated</span>}
          {visit.quotation_status === 'sent' && (
            <span className="chip chip-green">Quotation sent{visit.quotation_sent_via ? ` · ${visit.quotation_sent_via}` : ''}</span>
          )}
          {visit.quotation_status === 'reviewed' && <span className="chip">Final review</span>}
          {!visit.quotation_status && (
            <button
              className="btn btn-primary btn-sm"
              title="Open quotation page"
              onClick={() => openQuotationPage(visit)}
            >
              <FileText size={12} /> Generate quotation
            </button>
          )}
        </>
      )}
      {visit.status === 'planned' && (
        <button
          className="btn btn-success btn-sm"
          title="Start ride and track KMs"
          onClick={() => handleStartVisit(visit.id, 'ride')}
        >
          <Play size={12} /> Start Ride
        </button>
      )}

      {visit.status === 'in_progress' ? (
        <button
          className="btn btn-warning btn-sm"
          title="Mark reached school"
          onClick={() => handleEndRide(visit)}
        >
          <CheckCircle size={12} /> Reached School
        </button>
      ) : visit.status === 'planned' ? (
        <button
          className="btn btn-secondary btn-sm"
          disabled
          style={{ opacity: 0.45, cursor: 'not-allowed' }}
          title="Click Start Ride first"
        >
          <CheckCircle size={12} /> Reached School
        </button>
      ) : null}

      {visit.status === 'in_progress' && (
        <button
          className={`btn btn-sm ${activeRoute?.visitId === visit.id && routePanelOpen ? 'btn-secondary' : 'btn-primary'}`}
          title={activeRoute?.visitId === visit.id && routePanelOpen ? 'Hide route' : 'Show directions'}
          onClick={() => toggleRouteGuide(visit)}
        >
          <Navigation size={12} />
          {activeRoute?.visitId === visit.id && routePanelOpen ? 'Hide Route' : 'Show Route'}
        </button>
      )}

      {visit.status === 'arrived' && (
        <button
          className="btn btn-success btn-sm"
          title="Start school time"
          onClick={() => handleStartVisit(visit.id, 'school')}
        >
          <Play size={12} /> Start School
        </button>
      )}

      {visit.status === 'at_school' && (
        <button
          className="btn btn-warning btn-sm"
          title="End school visit"
          onClick={() => handleEndSchoolVisit(visit)}
        >
          <CheckCircle size={12} /> End Visit
        </button>
      )}

      {visit.status === 'visited' && !String(visit.id).startsWith('support-') && (
        <button
          className="btn btn-primary btn-sm"
          title={isSupportVisit(visit) ? 'Edit checklist / client feedback' : 'Edit visit review / interest details'}
          onClick={() => openPostVisitForm(visit)}
        >
          <Edit size={12} /> Edit Review
        </button>
      )}

      {!String(visit.id).startsWith('support-') && (
      <button
        className="btn btn-primary btn-sm"
        title="Capture and Upload Media"
        onClick={() => startCaptureAndUpload(visit)}
      >
        <Camera size={13} /> Capture
      </button>
      )}

      {!String(visit.id).startsWith('support-') && (
      <button
        className="btn btn-secondary btn-sm"
        title="View Media Gallery"
        onClick={() => openMediaModal(visit)}
      >
        <Image size={13} /> Gallery
      </button>
      )}

      {!(myVisitsOnly && visit.status === 'visited') && !String(visit.id).startsWith('support-') && (
        <button
          className="btn btn-danger btn-sm btn-icon"
          onClick={() => handleDeleteVisit(visit.id)}
          title="Delete visit"
        >
          <Trash2 size={13} />
        </button>
      )}
    </div>
  )

  const visitStatusRank = (status) => {
    if (status === 'in_progress') return 0   // Riding
    if (status === 'riding_home') return 1   // Riding Home
    if (status === 'at_school') return 2
    if (status === 'arrived') return 3
    if (status === 'planned') return 4
    return 5 // visited / other
  }

  const groupedVisitDays = Object.entries(
    visits.reduce((acc, visit) => {
      const rawDate = visit.planned_date ? visit.planned_date.substring(0, 10) : 'Unplanned'
      if (!acc[rawDate]) acc[rawDate] = []
      acc[rawDate].push(visit)
      return acc
    }, {})
  )
    .map(([date, dayVisits]) => {
      if (!myVisitsOnly) return [date, dayVisits]
      const sorted = [...dayVisits].sort((a, b) => {
        const rank = visitStatusRank(a.status) - visitStatusRank(b.status)
        if (rank !== 0) return rank
        // Planned stops stay in the route order they were saved in.
        if (a.status === 'planned' && b.status === 'planned') {
          const timeA = a.created_at ? new Date(a.created_at).getTime() : 0
          const timeB = b.created_at ? new Date(b.created_at).getTime() : 0
          if (timeA !== timeB) return timeA - timeB
          return (Number(a.id) || 0) - (Number(b.id) || 0)
        }
        return String(a.area_name || '').localeCompare(String(b.area_name || ''))
      })
      return [date, sorted]
    })
    .sort(([dateA, visitsA], [dateB, visitsB]) => {
      if (myVisitsOnly) {
        const aRiding = visitsA.some((v) => v.status === 'in_progress' || v.status === 'riding_home')
        const bRiding = visitsB.some((v) => v.status === 'in_progress' || v.status === 'riding_home')
        if (aRiding !== bRiding) return aRiding ? -1 : 1
      }
      if (dateA === 'Unplanned') return 1
      if (dateB === 'Unplanned') return -1
      return dateA.localeCompare(dateB)
    })

  const selectedYearName = academicYears.find(y => String(y.id) === String(selectedAcademicYear))?.name || ''

  return (
    <div className={`visits-page ${myVisitsOnly ? 'visits-page--my' : 'visits-page--overview'}`}>
      <div className="page-header">
        <div>
          <h1 className="page-title">{myVisitsOnly ? 'My Visits' : 'Visits'}</h1>
          <div className="segmented-control visit-kind-tabs" style={{ marginTop: 10 }}>
            {[
              { value: 'sales', label: 'Sales visits' },
              { value: 'support', label: 'Support visits' },
            ].map(({ value, label }) => (
              <button
                key={value}
                type="button"
                className={`btn ${visitKind === value ? 'btn-primary' : 'btn-secondary'} btn-sm`}
                onClick={() => {
                  setVisitKind(value)
                  setCurrentPage(1)
                  setFilterStatus('')
                }}
              >
                {label}
              </button>
            ))}
          </div>
          <p className="page-subtitle">
            {selectedYearName
              ? (myVisitsOnly
                ? `Your visits for academic year ${selectedYearName}`
                : `Visits for academic year ${selectedYearName}`)
              : 'Choose an academic year to view visits'}
          </p>
        </div>
        {myVisitsOnly && (
          <div className="page-header-actions">
            {renderHomeCommuteButton()}
            {renderLunchBreakButton()}
            <button className="btn btn-secondary" onClick={() => { setError(''); setShowMoveModal(true) }}>
              <Calendar size={16} /> Move Visits
            </button>
            <button className="btn btn-secondary" onClick={() => setShowAddServiceModal(true)}>
              <Plus size={16} /> Add Service
            </button>
            <button className="btn btn-primary" onClick={openAddModal}>
              <Plus size={16} /> New Visit
            </button>
          </div>
        )}
      </div>

      {/* Admin Only: Monthly Travel — overview page */}
      {!myVisitsOnly && isAdmin && (
        <div className="stats-grid" style={{ marginBottom: 20 }}>
          <div className="stat-card" style={{ minHeight: 'auto', padding: '18px 20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, width: '100%' }}>
              <div>
                <div className="stat-title" style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                  Monthly {filterTransport === 'bus' ? 'Bus Charge' : 'Bike Travel'}
                  <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                    <Calendar size={13} style={{ position: 'absolute', left: 8, pointerEvents: 'none', color: 'var(--accent-blue)' }} />
                    <input
                      type="month"
                      className="form-control"
                      value={bikeTravelMonth}
                      onChange={(e) => setBikeTravelMonth(e.target.value)}
                      style={{ width: 150, height: 30, padding: '4px 8px 4px 28px', fontSize: 12 }}
                    />
                  </div>
                </div>
                <div className={`stat-value ${filterTransport === 'bus' ? 'orange' : 'blue'}`} style={{ fontSize: 32, margin: '6px 0' }}>
                  {fetchingBikeTravel ? '—' : (filterTransport === 'bus' ? `₹${monthlyBusCharge}` : monthlyBikeTravelKm)}
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', marginLeft: 8 }}>
                    {filterTransport === 'bus' ? 'rupees' : 'km'}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  {calculatedMonthLabel}{selectedYearName ? ` · ${selectedYearName}` : ''}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div className="chip" style={{ marginBottom: 8 }}>Admin</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}>
                  Sales team field travel
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card visits-filters-card" style={{ marginBottom: 20 }}>
        <div className="filter-toolbar">
          <div className="segmented-control">
            {(visitKind === 'support'
              ? [
                { value: '', label: 'All' },
                { value: 'pending', label: 'Pending' },
                { value: 'visited', label: 'Visited' },
                { value: 'postponed', label: 'Postponed' },
              ]
              : [
                { value: '', label: 'All' },
                { value: 'planned', label: 'Planned' },
                { value: 'in_progress', label: 'Riding' },
                { value: 'arrived', label: 'Arrived' },
                { value: 'at_school', label: 'In School' },
                { value: 'visited', label: 'Visited' },
                { value: 'riding_home', label: 'Riding Home' },
                { value: 'followups', label: 'Follow-ups' },
              ]
            ).map(({ value, label }) => (
              <button
                key={value || 'all'}
                className={`btn ${filterStatus === value ? 'btn-primary' : 'btn-secondary'} btn-sm`}
                onClick={() => { setFilterStatus(value); setCurrentPage(1); }}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="filter-toolbar-group">
            <div className="filter-field">
              <span className="filter-label"><Filter size={14} /> Year</span>
              <select
                className="form-control filter-control"
                value={selectedAcademicYear}
                onChange={(e) => {
                  setSelectedAcademicYear(e.target.value)
                  setCurrentPage(1)
                  if (myVisitsOnly) {
                    const today = new Date().toLocaleDateString('en-CA')
                    setFilterDateFrom(today)
                    setFilterDateTo(today)
                  } else {
                    setFilterDateFrom('')
                    setFilterDateTo('')
                  }
                }}
              >
                {academicYears.length === 0 && <option value="">Loading...</option>}
                {academicYears.map(year => (
                  <option key={year.id} value={String(year.id)}>{year.name}</option>
                ))}
              </select>
            </div>

            {filterStatus !== 'followups' && (
              <div className="filter-field filter-field--dates">
                <span className="filter-label"><Calendar size={14} /> Date range</span>
                <div className="filter-date-range">
                  <input
                    type="date"
                    className="form-control filter-control"
                    title="From date"
                    value={filterDateFrom}
                    max={filterDateTo || undefined}
                    onChange={(e) => { setFilterDateFrom(e.target.value); setCurrentPage(1); }}
                  />
                  <span className="filter-date-sep">to</span>
                  <input
                    type="date"
                    className="form-control filter-control"
                    title="To date"
                    value={filterDateTo}
                    min={filterDateFrom || undefined}
                    onChange={(e) => { setFilterDateTo(e.target.value); setCurrentPage(1); }}
                  />
                  {(() => {
                    const today = new Date().toLocaleDateString('en-CA')
                    const showReset = myVisitsOnly
                      ? (filterDateFrom !== today || filterDateTo !== today)
                      : Boolean(filterDateFrom || filterDateTo)
                    if (!showReset) return null
                    return (
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => {
                        if (myVisitsOnly) {
                          setFilterDateFrom(today)
                          setFilterDateTo(today)
                        } else {
                          setFilterDateFrom('')
                          setFilterDateTo('')
                        }
                        setCurrentPage(1)
                      }}
                      title={myVisitsOnly ? 'Reset to today' : 'Clear date range'}
                    >
                      <X size={12} /> {myVisitsOnly ? 'Today' : 'Clear'}
                    </button>
                    )
                  })()}
                </div>
              </div>
            )}

            {isAdmin && !myVisitsOnly && filterStatus !== 'followups' && (
              <div className="filter-field">
                <span className="filter-label"><User size={14} /> Person</span>
                <select
                  className="form-control filter-control"
                  value={filterPerson}
                  onChange={(e) => { setFilterPerson(e.target.value); setCurrentPage(1); }}
                >
                  <option value="">All Persons</option>
                  {persons.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
            )}

            {myVisitsOnly && filterStatus !== 'followups' && (
              <div className="filter-field">
                <span className="filter-label">Transport</span>
                <select
                  className="form-control filter-control"
                  value={filterTransport}
                  onChange={(e) => { setFilterTransport(e.target.value); setCurrentPage(1); }}
                >
                  <option value="bike">Bike</option>
                  <option value="bus">Bus</option>
                </select>
              </div>
            )}
          </div>
        </div>
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}

      {/* In-app navigation (map + turn-by-turn, no Google Maps) */}
      {activeRoute && routePanelOpen && (
        <div
          className={`gmaps-start-ride-overlay ${routeFullscreen ? 'is-fullscreen' : ''}`}
          style={{ marginBottom: routeFullscreen ? 0 : 16 }}
        >
          {!routeFullscreen && (
            <div className="gmaps-start-ride-topbar">
              <button
                type="button"
                className="gmaps-icon-btn"
                onClick={() => { setActiveRoute(null); setRoutePanelOpen(false); setRouteFullscreen(false) }}
                title="Close"
              >
                <X size={18} />
              </button>
              <div className="gmaps-start-ride-topbar-title">
                <span>Directions</span>
                <small>{activeRoute.schoolName}</small>
              </div>
              <button
                type="button"
                className="gmaps-icon-btn"
                onClick={() => setRouteFullscreen(true)}
                title="Full screen"
              >
                <Maximize2 size={18} />
              </button>
            </div>
          )}
          <InAppDirectionsScreen
            key={`nav-${routeMountKey}`}
            route={activeRoute}
            fullscreen={routeFullscreen}
            onClose={() => { setRoutePanelOpen(false); setRouteFullscreen(false) }}
          />
        </div>
      )}

      {/* Collapsed route chip when hidden but still active */}
      {activeRoute && !routePanelOpen && (
        <div className="gmaps-route-chip-bar" style={{ marginBottom: 16 }}>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              setRouteMountKey((k) => k + 1)
              setRoutePanelOpen(true)
              setRouteFullscreen(true)
            }}
          >
            <Navigation size={14} /> Show route to {activeRoute.schoolName}
          </button>
          <button
            type="button"
            className="btn btn-secondary btn-icon"
            onClick={() => { setActiveRoute(null); setRouteFullscreen(false) }}
            title="Dismiss"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Visits or Follow-ups List */}
      {loading ? (
        <div className="loading"><div className="spinner" /><span>Loading data...</span></div>
      ) : filterStatus === 'followups' ? (
        /* Followups Tab */
        followups.length === 0 ? (
          <div className="empty-state">
            <Clock size={48} />
            <h3>No follow-ups found</h3>
            <p>Log a follow-up for a visit to see it here</p>
          </div>
        ) : (
          <div className="card">
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Planned Date</th>
                    <th>Visited Date</th>
                    <th>Person</th>
                    <th>School / College Name</th>
                    <th>Strength of Students</th>
                    <th>Follow-up Date</th>
                    <th>Purpose of Next Visit</th>
                    <th>Interested Services</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {followups.map(item => (
                    <tr key={item.id}>
                      <td>
                        {item.visit_detail?.planned_date ? (
                          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <Calendar size={13} style={{ color: 'var(--text-muted)' }} />
                            {new Date(item.visit_detail.planned_date).toLocaleDateString()}
                          </span>
                        ) : '—'}
                      </td>
                      <td>
                        {item.visit_detail?.visited_date ? (
                          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <Calendar size={13} style={{ color: 'var(--text-muted)' }} />
                            {new Date(item.visit_detail.visited_date).toLocaleDateString()}
                          </span>
                        ) : '—'}
                      </td>
                      <td style={{ fontWeight: 500, color: 'var(--text-primary)' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <User size={13} style={{ color: 'var(--text-muted)' }} />
                          {item.visit_detail?.person_detail?.name || '—'}
                        </span>
                      </td>
                      <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                        {schoolName(item.visit_detail?.area_name) || '—'}
                      </td>
                      <td style={{ fontWeight: 600 }}>
                        {item.strength ? `${item.strength} Students` : '—'}
                      </td>
                      <td style={{ fontWeight: 600, color: 'var(--accent-blue)' }}>
                        {item.followup_date ? (
                          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <Calendar size={13} />
                            {new Date(item.followup_date).toLocaleDateString()}
                          </span>
                        ) : '—'}
                      </td>
                      <td style={{ whiteSpace: 'pre-wrap' }}>
                        {item.next_purpose}
                      </td>
                      <td style={{ color: 'var(--accent-blue)', fontWeight: 500 }}>
                        {item.visit_detail?.interested_services || '—'}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => handleDeleteFollowup(item.id)}
                          title="Delete follow-up record"
                        >
                          <Trash2 size={13} /> Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      ) : visits.length === 0 ? (
        <div className="empty-state">
          <MapPin size={48} />
          <h3>No visits found</h3>
          <p>{myVisitsOnly ? 'No visits are assigned to you yet' : 'No visits found for the selected filters'}</p>
        </div>
      ) : !myVisitsOnly ? (
        /* Overview: key visit info (no action buttons) */
        <div className="card">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Assigned To</th>
                  <th>School / College</th>
                  <th>Status</th>
                  <th>Planned Date</th>
                  <th>Created Date</th>
                  <th>Visited Date</th>
                  <th>Interest</th>
                </tr>
              </thead>
              <tbody>
                {visits.map((visit) => {
                  const createdColor = createdDateColor(visit.created_at)
                  return (
                  <tr
                    key={visit.id}
                    className={createdColor ? 'visit-created-row' : undefined}
                    style={createdColor ? { '--created-bg': createdColor.bg } : undefined}
                  >
                    <td style={{ fontWeight: 500, color: 'var(--text-primary)' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <User size={13} style={{ color: 'var(--text-muted)' }} />
                        {visit.person_detail?.name || '—'}
                      </span>
                      {visit.person_detail?.designation && (
                        <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px', marginLeft: '19px' }}>
                          {visit.person_detail.designation}
                        </div>
                      )}
                    </td>
                    <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                      <div>
                        {schoolName(visit.area_name) || '—'}
                        {isSupportVisit(visit) && <SupportVisitChip />}
                      </div>
                      {visit.contact_number && (
                        <div style={{ fontSize: '11px', color: 'var(--accent-blue)', fontWeight: 600, marginTop: '4px', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <Phone size={11} /> {visit.contact_number}
                        </div>
                      )}
                    </td>
                    <td>{statusBadge(visit.status)}</td>
                    <td>
                      {visit.planned_date ? (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Calendar size={13} style={{ color: 'var(--text-muted)' }} />
                          {formatVisitDate(visit.planned_date)}
                        </span>
                      ) : '—'}
                    </td>
                    <td>
                      <CreatedDateLabel value={visit.created_at} />
                    </td>
                    <td>
                      {visit.visited_date ? (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Calendar size={13} style={{ color: 'var(--text-muted)' }} />
                          {formatVisitDate(visit.visited_date)}
                        </span>
                      ) : '—'}
                    </td>
                    <td>
                      {visit.interest_status && visit.interest_status !== 'pending' ? (
                        <span className="chip chip-slate" style={{ textTransform: 'capitalize' }}>
                          {String(visit.interest_status).replace(/_/g, ' ')}
                        </span>
                      ) : '—'}
                    </td>
                  </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          {totalPages > 0 && visits.length > 0 && (
            <div className="pagination-bar">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ color: 'var(--text-secondary)' }}>Rows per page:</span>
                <select
                  className="form-control"
                  value={itemsPerPage}
                  onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                  style={{ width: 'auto', border: '1px solid var(--border-color)', padding: '4px 8px', height: 32, fontSize: 13 }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                  Page {currentPage} of {totalPages}{totalItems ? ` · ${totalItems} total` : ''}
                </span>
                <button className="btn btn-secondary btn-sm" disabled={currentPage <= 1} onClick={() => setCurrentPage(p => p - 1)}>
                  <ChevronLeft size={14} />
                </button>
                <button className="btn btn-secondary btn-sm" disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)}>
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="card visits-list-card">
          {/* Desktop table */}
          <div className="table-container visits-desktop-table">
            <table className="table">
              <thead>
                <tr>
                  <th>Person</th>
                  <th>School / College Name</th>
                  <th>Status</th>
                  <th>Interest</th>
                  <th>Planned Date</th>
                  <th>Created Date</th>
                  <th>Visited Date</th>
                  <th>{filterTransport === 'bus' ? 'Bus' : 'Distance'}</th>
                  <th>Time in School</th>
                  <th>Media Count</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {groupedVisitDays.map(([dateStr, dayVisits], index) => {
                    const byPerson = Object.entries(
                      dayVisits.reduce((acc, v) => {
                        const personKey = v.person_detail?.name || 'Unknown'
                        if (!acc[personKey]) acc[personKey] = []
                        acc[personKey].push(v)
                        return acc
                      }, {})
                    )
                    return (
                      <React.Fragment key={dateStr}>
                        <tr className="table-group-row">
                          <td colSpan="11">
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                <Calendar size={14} style={{ color: 'var(--accent-blue)' }} />
                                {dateStr === 'Unplanned'
                                  ? 'Unplanned / No Date'
                                  : `Day ${index + 1} — ${new Date(dateStr).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' })}`}
                              </span>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                <span className="table-group-meta" style={{ marginLeft: 0 }}>
                                  ({dayVisits.filter((visit) => !isHomeCommute(visit)).length} visits · {byPerson.length} person{byPerson.length > 1 ? 's' : ''})
                                </span>
                                {myVisitsOnly && dateStr !== 'Unplanned' && (
                                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => openDayPath(dateStr)}>
                                    <Navigation size={13} /> Show path
                                  </button>
                                )}
                              </span>
                            </div>
                          </td>
                        </tr>
                        {byPerson.map(([personName, personVisits]) => (
                          <React.Fragment key={personName}>
                            <tr className="table-group-row">
                              <td colSpan="11">
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                    <User size={14} style={{ color: 'var(--accent-blue)' }} />
                                    {personName}
                                    <span className="table-group-meta" style={{ marginLeft: 0 }}>
                                      ({personVisits.filter((visit) => !isHomeCommute(visit)).length} visit{personVisits.filter((visit) => !isHomeCommute(visit)).length === 1 ? '' : 's'})
                                    </span>
                                  </div>
                                  <span className="chip chip-green">
                                    {filterTransport === 'bus'
                                      ? `Day charge: ₹${getPersonDayBusCharge(personVisits)}`
                                      : `Day travel: ${getPersonDayDistance(personVisits)} km`}
                                  </span>
                                </div>
                              </td>
                            </tr>
                            {personVisits.filter((visit) => !isHomeCommute(visit)).map((visit) => {
                              const plannedStops = personVisits.filter((v) => v.status === 'planned' && !isHomeCommute(v))
                              const plannedOrder = visit.status === 'planned'
                                ? plannedStops.findIndex((v) => v.id === visit.id) + 1
                                : 0
                              return (
                              <tr key={visit.id}>
                                <td style={{ fontWeight: 500, color: 'var(--text-primary)' }}>
                                  <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <User size={13} style={{ color: 'var(--text-muted)' }} />
                                    {visit.person_detail?.name || '—'}
                                  </span>
                                  {visit.person_detail?.designation && (
                                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px', marginLeft: '19px' }}>
                                      {visit.person_detail.designation}
                                    </div>
                                  )}
                                </td>
                                <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    {plannedOrder > 0 && (
                                      <span className="chip chip-slate" title="Planned order">{plannedOrder}</span>
                                    )}
                                    <span>
                                      {schoolName(visit.area_name)}
                                      {isSupportVisit(visit) && <SupportVisitChip />}
                                    </span>
                                  </div>
                                  {visit.contact_number && (
                                    <div style={{ fontSize: '11px', color: 'var(--accent-blue)', fontWeight: 600, marginTop: '4px', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                      <Phone size={11} /> {visit.contact_number}
                                    </div>
                                  )}
                                  {visit.interested_services && (
                                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '6px' }}>
                                      {visit.interested_services.split(',').map((s, idx) => s.trim() ? (
                                        <span key={idx} className="chip">{s.trim()}</span>
                                      ) : null)}
                                    </div>
                                  )}
                                </td>
                                <td>{statusBadge(visit.status)}</td>
                                <td>{interestBadge(visit.interest_status)}</td>
                                <td>
                                  {visit.planned_date ? (
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                      <Calendar size={13} style={{ color: 'var(--text-muted)' }} />
                                      {formatVisitDate(visit.planned_date)}
                                    </span>
                                  ) : '—'}
                                </td>
                                <td>
                                  <CreatedDateLabel value={visit.created_at} />
                                </td>
                                <td>
                                  {visit.visited_date ? (
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                      <Calendar size={13} style={{ color: 'var(--text-muted)' }} />
                                      {formatVisitDate(visit.visited_date)}
                                    </span>
                                  ) : '—'}
                                </td>
                                <td>
                                  <span className={`chip ${visit.status === 'in_progress' ? 'chip-orange' : ''}`}>
                                    {visit.transport_mode === 'bus' || filterTransport === 'bus'
                                      ? `₹${visit.bus_charge || 0}`
                                      : `${getVisitDistance(visit)} km`}
                                  </span>
                                </td>
                                <td style={{ textAlign: 'center' }}>
                                  <span className={`chip ${visit.status === 'at_school' ? 'chip-warm' : 'chip-slate'}`}>
                                    {visit.status === 'at_school'
                                      ? formatTimeSpent(getLiveSchoolMinutes(visit) ?? 0)
                                      : visit.status === 'visited'
                                        ? formatTimeSpent(visit.time_spent_minutes)
                                        : '—'}
                                  </span>
                                </td>
                                <td style={{ textAlign: 'center' }}>
                                  <span className="chip chip-slate">{visit.media_count || 0}</span>
                                </td>
                                <td style={{ textAlign: 'right' }}>
                                  {renderVisitActions(visit)}
                                </td>
                              </tr>
                              )
                            })}
                          </React.Fragment>
                        ))}
                      </React.Fragment>
                    )
                  })}
              </tbody>
            </table>
          </div>

          {/* Mobile card list */}
          <div className="visits-mobile-list">
            {groupedVisitDays.map(([dateStr, dayVisits], index) => {
              const byPerson = Object.entries(
                dayVisits.reduce((acc, v) => {
                  const personKey = v.person_detail?.name || 'Unknown'
                  if (!acc[personKey]) acc[personKey] = []
                  acc[personKey].push(v)
                  return acc
                }, {})
              )
              return (
                <div key={dateStr} className="visits-mobile-day">
                  <div className="visits-mobile-day-header">
                    <div className="visits-mobile-day-title">
                      <Calendar size={14} />
                      <span>
                        {dateStr === 'Unplanned'
                          ? 'Unplanned / No Date'
                          : `Day ${index + 1} — ${new Date(dateStr).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}`}
                      </span>
                    </div>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <span className="visits-mobile-day-meta">
                        {dayVisits.filter((visit) => !isHomeCommute(visit)).length} visit{dayVisits.filter((visit) => !isHomeCommute(visit)).length === 1 ? '' : 's'}
                      </span>
                      {myVisitsOnly && dateStr !== 'Unplanned' && (
                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => openDayPath(dateStr)}>
                          <Navigation size={13} /> Show path
                        </button>
                      )}
                    </span>
                  </div>

                  {byPerson.map(([personName, personVisits]) => (
                    <div key={personName} className="visits-mobile-person-block">
                      <div className="visits-mobile-person-header">
                        <span><User size={13} /> {personName}</span>
                        <span className="chip chip-green">
                          {filterTransport === 'bus'
                            ? `₹${getPersonDayBusCharge(personVisits)}`
                            : `${getPersonDayDistance(personVisits)} km`}
                        </span>
                      </div>

                      {personVisits.filter((visit) => !isHomeCommute(visit)).map((visit) => {
                        const plannedStops = personVisits.filter((v) => v.status === 'planned' && !isHomeCommute(v))
                        const plannedOrder = visit.status === 'planned'
                          ? plannedStops.findIndex((v) => v.id === visit.id) + 1
                          : 0
                        return (
                        <div key={visit.id} className="visit-mobile-card">
                          <div className="visit-mobile-card-top">
                            <div className="visit-mobile-card-title">
                              {plannedOrder > 0 && (
                                <span className="chip chip-slate" style={{ marginRight: 6 }} title="Planned order">{plannedOrder}</span>
                              )}
                              {schoolName(visit.area_name) || '—'}
                              {isSupportVisit(visit) && <SupportVisitChip />}
                            </div>
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                              {statusBadge(visit.status)}
                              {interestBadge(visit.interest_status)}
                            </div>
                          </div>

                          <div className="visit-mobile-card-meta">
                            {visit.contact_number && (
                              <span><Phone size={11} /> {visit.contact_number}</span>
                            )}
                            <span>
                              {filterTransport === 'bus' || visit.transport_mode === 'bus'
                                ? `₹${visit.bus_charge || 0}`
                                : `${getVisitDistance(visit)} km`}
                            </span>
                            <span>
                              {visit.status === 'at_school'
                                ? formatTimeSpent(getLiveSchoolMinutes(visit) ?? 0)
                                : visit.status === 'visited'
                                  ? formatTimeSpent(visit.time_spent_minutes)
                                  : '—'}
                            </span>
                            <span>Planned {formatVisitDate(visit.planned_date)}</span>
                            <CreatedDateLabel value={visit.created_at} />
                            <span>Visited {formatVisitDate(visit.visited_date)}</span>
                            <span>{visit.media_count || 0} media</span>
                          </div>

                          {visit.interested_services && (
                            <div className="visit-mobile-card-chips">
                              {visit.interested_services.split(',').map((s, idx) => s.trim() ? (
                                <span key={idx} className="chip">{s.trim()}</span>
                              ) : null)}
                            </div>
                          )}

                          {renderVisitActions(visit)}
                        </div>
                        )
                      })}
                    </div>
                  ))}
                </div>
              )
            })}
          </div>
          {totalPages > 0 && filterStatus !== 'followups' && visits.length > 0 && (
            <div className="pagination-bar">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ color: 'var(--text-secondary)' }}>Rows per page:</span>
                <select
                  className="form-control"
                  value={itemsPerPage}
                  onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                  style={{ width: 'auto', border: '1px solid var(--border-color)', padding: '4px 8px', height: 32, fontSize: 13 }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
              <span style={{ color: 'var(--text-secondary)' }}>
                {totalItems > 0
                  ? `${Math.min((currentPage - 1) * itemsPerPage + 1, totalItems)}–${Math.min(currentPage * itemsPerPage, totalItems)} of ${totalItems}`
                  : `Page ${currentPage} of ${totalPages}`
                }
              </span>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  className="btn btn-secondary btn-sm btn-icon"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  style={{ opacity: currentPage === 1 ? 0.4 : 1 }}
                >
                  <ChevronLeft size={16} />
                </button>
                <button
                  className="btn btn-secondary btn-sm btn-icon"
                  disabled={currentPage === totalPages || totalPages === 0}
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  style={{ opacity: currentPage === totalPages || totalPages === 0 ? 0.4 : 1 }}
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {dayPath?.loading && (
        <div className="modal-overlay" style={{ zIndex: 1500 }}>
          <div className="modal" style={{ maxWidth: 420 }}>
            <div className="modal-body" style={{ fontSize: 14, color: 'var(--text-secondary)' }}>
              Loading this day’s visits…
            </div>
          </div>
        </div>
      )}
      {dayPath && !dayPath.loading && (
        <DayVisitPathMap
          title={dayPath.title}
          stops={dayPath.stops}
          onClose={() => setDayPath(null)}
        />
      )}

      {showMoveModal && (
        <div className="modal-overlay" style={{ zIndex: 1400 }}>
          <div className="modal" style={{ maxWidth: '440px' }}>
            <div className="modal-header">
              <h3 className="modal-title">Move Visits</h3>
              <button className="modal-close" onClick={() => setShowMoveModal(false)}><X size={20} /></button>
            </div>
            <form onSubmit={handleMoveVisits}>
              <div className="modal-body">
                {error && <div className="alert alert-error" style={{ marginBottom: 10 }}>{error}</div>}
                <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 0 }}>
                  Plans visits from earlier days that are still not visited. They stay in the same order and fill each day up to the number of institutes you set. Sundays are skipped.
                </p>
                <div style={{ display: 'grid', gap: 12 }}>
                  <div>
                    <label className="form-label" style={{ fontSize: '12px' }}>Start date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={moveDate}
                      onChange={(e) => setMoveDate(e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <label className="form-label" style={{ fontSize: '12px' }}>Institutes per day</label>
                    <input
                      type="number"
                      className="form-control"
                      min="1"
                      max="50"
                      value={movePerDay}
                      onChange={(e) => setMovePerDay(e.target.value)}
                      required
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowMoveModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={movingVisits}>
                  {movingVisits ? 'Planning...' : 'Plan visits'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Service Modal */}
      {showAddServiceModal && (
        <div className="modal-overlay" style={{ zIndex: 1400 }}>
          <div className="modal" style={{ maxWidth: '400px' }}>
            <div className="modal-header">
              <h3 className="modal-title">Add Interested Service</h3>
              <button className="modal-close" onClick={() => setShowAddServiceModal(false)}><X size={20} /></button>
            </div>
            <div className="modal-body">
              <label className="form-label" style={{ fontSize: '12px' }}>Service Name</label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g. Workshop, Training"
                value={newServiceName}
                onChange={e => setNewServiceName(e.target.value)}
              />
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowAddServiceModal(false)}>Cancel</button>
              <button type="button" className="btn btn-primary" onClick={() => {
                if (newServiceName.trim() && !masterServices.includes(newServiceName.trim())) {
                  const updated = [...masterServices, newServiceName.trim()];
                  setMasterServices(updated);
                  localStorage.setItem('edubricz_custom_services', JSON.stringify(updated));
                }
                setNewServiceName('');
                setShowAddServiceModal(false);
              }}>Save</button>
            </div>
          </div>
        </div>
      )}

      {locationPick && (
        <div className="modal-overlay" style={{ zIndex: 1600 }}>
          <div className="modal" style={{ maxWidth: 720, width: '100%' }}>
            <div className="modal-header">
              <h2 className="modal-title">Pick institute location</h2>
              <button type="button" className="btn btn-secondary btn-icon" onClick={() => !savingLocation && setLocationPick(null)}>
                <X size={16} />
              </button>
            </div>
            <div className="modal-body">
              <style>{`.pac-container{z-index:2000 !important;}`}</style>
              <p style={{ marginTop: 0, color: 'var(--text-secondary)' }}>
                {schoolName(locationPick.visit.area_name) || 'This institute'} has no location saved. Search Google Maps or click the map to drop a pin, then save it and start the ride.
              </p>
              <GoogleLocationSearch
                getOrigin={() => (
                  locationPick?.latitude
                    ? { lat: Number(locationPick.latitude), lng: Number(locationPick.longitude) }
                    : null
                )}
                onPlace={(point) => setLocationPick((prev) => prev ? { ...prev, ...point } : prev)}
              />
              <div style={{ height: 360, borderRadius: 8, overflow: 'hidden' }}>
                <GoogleClickMap
                  center={[Number(locationPick.latitude), Number(locationPick.longitude)]}
                  marker
                  height={360}
                  onPick={(point) => setLocationPick((prev) => prev ? { ...prev, ...point, address: '' } : prev)}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" disabled={savingLocation} onClick={() => setLocationPick(null)}>Cancel</button>
              <button type="button" className="btn btn-success" disabled={savingLocation} onClick={confirmSupportLocation}>
                {savingLocation ? 'Saving...' : 'Save location and start ride'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Visit Modal */}
      {showAddModal && (
        <div className="modal-overlay" style={{ zIndex: 1300 }}>
          <div className="modal" style={{ maxWidth: '540px', width: '95%' }}>
            <div className="modal-header">
              <h2 className="modal-title">{editingVisitId ? 'Edit Planned Visit' : 'Plan New Visit'}</h2>
              <button className="modal-close" onClick={() => setShowAddModal(false)}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleAddVisit}>
              <div className="modal-body" style={{ padding: '14px 18px', maxHeight: '75vh', overflowY: 'auto' }}>
                {error && <div className="alert alert-error" style={{ marginBottom: 10, padding: '6px 10px', fontSize: '13px' }}>{error}</div>}

                {/* Row 1: Person (edit only) + Area */}
                <div style={{ display: 'grid', gridTemplateColumns: editingVisitId ? '1fr 1fr' : '1fr', gap: '10px', marginBottom: '10px' }}>
                  {editingVisitId && (
                  <div>
                    <label className="form-label" style={{ fontSize: '12px', marginBottom: '4px' }}>Person *</label>
                    <select
                      className="form-control"
                      name="person"
                      value={form.person}
                      onChange={handleFormChange}
                      required
                      style={{ fontSize: '13px', padding: '5px 8px', height: '34px' }}
                    >
                      <option value="">Select person...</option>
                      {persons.map(p => (
                        <option key={p.id} value={p.id}>
                          {p.name}{p.designation ? ` — ${p.designation}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  )}
                  <div>
                    <label className="form-label" style={{ fontSize: '12px', marginBottom: '4px' }}>School/College Name *</label>
                    <input
                      className="form-control"
                      name="area_name"
                      placeholder="e.g. Greenwood High School"
                      value={form.area_name}
                      onChange={handleFormChange}
                      required
                      style={{ fontSize: '13px', padding: '5px 8px', height: '34px' }}
                    />
                  </div>
                </div>

                {!editingVisitId && (
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
                      <label className="form-label" style={{ fontSize: '12px', marginBottom: 0 }}>
                        Nearby schools
                        {currentLocation ? ' · from your current location' : ''}
                      </label>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        style={{ fontSize: 12, whiteSpace: 'nowrap' }}
                        onClick={useCurrentLocationForSchool}
                        disabled={!currentLocation && !locationRef.current}
                      >
                        <MapPin size={12} /> Use current location
                      </button>
                    </div>
                    <input
                      className="form-control"
                      placeholder="Filter nearby schools..."
                      value={nearbyQuery}
                      onChange={(e) => setNearbyQuery(e.target.value)}
                      style={{ fontSize: 12, padding: '5px 8px', height: 32, marginBottom: 6 }}
                    />
                    <div style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: 6 }}>
                      {nearbyLoading && (
                        <div style={{ padding: 10, fontSize: 12, color: 'var(--text-secondary)' }}>Finding schools within 5 km…</div>
                      )}
                      {!nearbyLoading && nearbyError && nearbySchools.length === 0 && (
                        <div style={{ padding: 10, fontSize: 12, color: 'var(--text-secondary)' }}>{nearbyError}</div>
                      )}
                      {!nearbyLoading && nearbySchools
                        .filter((school) => {
                          const q = nearbyQuery.trim().toLowerCase()
                          if (!q) return true
                          return (school.name || '').toLowerCase().includes(q)
                            || (school.address || '').toLowerCase().includes(q)
                        })
                        .map((school) => {
                          const key = String(school.id || `${school.name}-${school.lat}-${school.lng}`)
                          const selected = key === selectedNearbyKey
                          return (
                            <button
                              key={key}
                              type="button"
                              onClick={() => selectNearbySchool(school)}
                              style={{
                                display: 'block',
                                width: '100%',
                                textAlign: 'left',
                                padding: '8px 10px',
                                border: 'none',
                                borderBottom: '1px solid var(--border-color)',
                                background: selected ? '#e8f1ff' : '#fff',
                                cursor: 'pointer',
                              }}
                            >
                              <div style={{ fontSize: 13, fontWeight: 600 }}>{school.name}</div>
                              <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                                {school.distance_km != null ? `${school.distance_km} km` : 'Nearby'}
                                {school.address ? ` · ${school.address}` : ''}
                              </div>
                            </button>
                          )
                        })}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                      Select a school, or type a new name and set its location with current location or the map.
                    </div>
                  </div>
                )}

                {/* Map section */}
                <div style={{ marginBottom: '10px' }}>
                  <label className="form-label" style={{ fontSize: '12px', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    School/College Location on Map
                    {locatingGPS && (
                      <span style={{ fontSize: '11px', color: 'var(--accent-blue)', fontWeight: 600, animation: 'pulse 1.2s ease-in-out infinite' }}>
                        📡 Locating your GPS...
                      </span>
                    )}
                  </label>
                  <div style={{ marginBottom: '6px' }}>
                    <style>{`.pac-container{z-index:2000 !important;}`}</style>
                    <GoogleLocationSearch
                      getOrigin={() => (
                        form.latitude
                          ? { lat: Number(form.latitude), lng: Number(form.longitude) }
                          : (searchCenter ? { lat: Number(searchCenter[0]), lng: Number(searchCenter[1]) } : null)
                      )}
                      onPlace={(point) => {
                        setSelectedNearbyKey('')
                        setSearchQuery(point.address || '')
                        setForm((prev) => ({
                          ...prev,
                          latitude: point.latitude,
                          longitude: point.longitude,
                          area_name: prev.area_name || (point.address || '').split(',')[0],
                        }))
                        setSearchCenter([Number(point.latitude), Number(point.longitude)])
                        if (!editingVisitId) loadNearbySchools(point.latitude, point.longitude)
                      }}
                    />
                  </div>
                  <div style={{ height: '180px', border: '1px solid var(--border-color)', borderRadius: '6px', overflow: 'hidden', position: 'relative' }}>
                    <GoogleClickMap
                      center={searchCenter || [13.0953, 77.3964]}
                      marker={Boolean(form.latitude && form.longitude)}
                      height={180}
                      onPick={({ latitude, longitude }) => {
                        setSelectedNearbyKey('')
                        setForm((prev) => ({ ...prev, latitude, longitude }))
                      }}
                    />
                  </div>
                  {locatingGPS ? (
                    <div style={{ fontSize: '11px', color: 'var(--accent-blue)', marginTop: '3px', fontWeight: 600 }}>
                      ⏳ Fetching your current location...
                    </div>
                  ) : form.latitude && form.longitude ? (
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '3px' }}>
                      📍 {form.latitude}, {form.longitude} — <span style={{ color: 'var(--accent-blue)', fontWeight: 600 }}>Click the map to adjust</span>
                    </div>
                  ) : (
                    <div style={{ fontSize: '11px', color: 'var(--accent-blue)', marginTop: '3px', fontWeight: 600 }}>
                      ⚠️ GPS unavailable · Click map or search to set location
                    </div>
                  )}
                </div>

                {/* Row 2: Planned Date + Interest Status */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                  <div>
                    <label className="form-label" style={{ fontSize: '12px', marginBottom: '4px' }}>Planned Date</label>
                    <input
                      className="form-control"
                      name="planned_date"
                      type="date"
                      value={form.planned_date}
                      onChange={handleFormChange}
                      style={{ fontSize: '13px', padding: '5px 8px', height: '34px' }}
                    />
                  </div>
                  <div>
                    <label className="form-label" style={{ fontSize: '12px', marginBottom: '4px' }}>Client Interest Status</label>
                    <select
                      className="form-control"
                      name="interest_status"
                      value={form.interest_status || 'pending'}
                      onChange={handleFormChange}
                      style={{ fontSize: '13px', padding: '5px 8px', height: '34px' }}
                    >
                      <option value="pending">Pending</option>
                      <option value="interested">Interested</option>
                      <option value="not_interested">Not Interested</option>
                    </select>
                  </div>
                </div>

                {/* Checkbox tasks to be done */}
                <div style={{ marginBottom: '12px', background: '#f8fafc', padding: '10px', borderRadius: '6px', border: '1px solid var(--border-color)' }}>
                  <label className="form-label" style={{ fontSize: '12px', fontWeight: 600, marginBottom: '6px', display: 'block' }}>
                    📋 Tasks to be done in this Visit:
                  </label>
                  <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', columnGap: '16px', rowGap: '6px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={form.wants_parents_orientation}
                        onChange={e => setForm(f => ({ ...f, wants_parents_orientation: e.target.checked }))}
                      />
                      Parents Orientation
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={form.wants_student_orientation}
                        onChange={e => setForm(f => ({ ...f, wants_student_orientation: e.target.checked }))}
                      />
                      Student Orientation
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={form.wants_staff_orientation}
                        onChange={e => setForm(f => ({ ...f, wants_staff_orientation: e.target.checked }))}
                      />
                      Staff Orientation
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={form.wants_revisit}
                        onChange={e => setForm(f => ({ ...f, wants_revisit: e.target.checked }))}
                      />
                      Follow-up Revisit
                    </label>
                  </div>

                  {/* DYNAMIC MASTER SERVICES CHECKBOXES */}
                  {masterServices.length > 0 && (
                    <div style={{ marginTop: '12px', borderTop: '1px dashed #cbd5e1', paddingTop: '10px' }}>
                      <label style={{ fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '6px', display: 'block' }}>
                        Additional Custom Services:
                        <span style={{ fontWeight: 'normal', fontSize: '11px', color: '#94a3b8', marginLeft: '6px' }}>(Defined via + Add Service)</span>
                      </label>
                      <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', columnGap: '16px', rowGap: '6px' }}>
                        {masterServices.map(srv => {
                          const currentArr = form.interested_services ? form.interested_services.split(',').map(s => s.trim()).filter(Boolean) : [];
                          const isChecked = currentArr.includes(srv);
                          return (
                            <label key={srv} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer', color: '#0369a1' }}>
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={e => {
                                  let newArr = [...currentArr];
                                  if (e.target.checked && !newArr.includes(srv)) {
                                    newArr.push(srv);
                                  } else if (!e.target.checked) {
                                    newArr = newArr.filter(x => x !== srv);
                                  }
                                  setForm(f => ({ ...f, interested_services: newArr.join(', ') }));
                                }}
                              />
                              {srv}
                            </label>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: '4px' }}>
                  <label className="form-label" style={{ fontSize: '12px', marginBottom: '4px' }}>Notes</label>
                  <textarea
                    className="form-control"
                    name="notes"
                    placeholder="Any additional notes..."
                    value={form.notes}
                    onChange={handleFormChange}
                    rows={3}
                    style={{ fontSize: '13px', padding: '5px 8px' }}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowAddModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : (editingVisitId ? 'Save Changes' : 'Create Visit')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}


      {/* Support visit end: checklist + client feedback */}
      {supportEnd && (
        <div className="modal-overlay" style={{ zIndex: 1300 }}>
          <div className="modal modal-lg">
            <div className="modal-header">
              <h2 className="modal-title">Support Visit — Checklist & Client Feedback</h2>
              <button className="modal-close" onClick={() => !supportEnd.saving && setSupportEnd(null)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <p style={{ margin: '0 0 12px', color: 'var(--text-secondary)', fontSize: 13 }}>
                {supportEnd.companyName}
              </p>

              <div className="form-group">
                <label className="form-label">Checklist</label>
                {supportEnd.loading ? (
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Loading checklist…</div>
                ) : !supportEnd.companyId ? (
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                    This institute is not linked to a client, so there is no checklist.
                  </div>
                ) : supportEnd.items.length === 0 ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: 'var(--text-secondary)' }}>
                    No checklist items assigned to this client.
                    <button type="button" className="btn btn-secondary btn-sm" onClick={assignAllSupportChecklist}>
                      Assign all
                    </button>
                  </div>
                ) : (
                  <div style={{ border: '1px solid #e2e8f0', borderRadius: 8 }}>
                    {supportEnd.items.map((ci, idx) => (
                      <div
                        key={ci.id}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                          padding: '8px 12px', borderTop: idx === 0 ? 'none' : '1px solid #f1f5f9', fontSize: 13,
                        }}
                      >
                        <span style={{ flex: 1 }}>{ci.checklist_item_name || `Item #${ci.checklist_item}`}</span>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                          <input
                            type="radio"
                            name={`support-checklist-${ci.id}`}
                            checked={!!ci.is_done}
                            onChange={() => toggleSupportChecklistItem(ci, true)}
                          />
                          Yes
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                          <input
                            type="radio"
                            name={`support-checklist-${ci.id}`}
                            checked={!ci.is_done}
                            onChange={() => toggleSupportChecklistItem(ci, false)}
                          />
                          No
                        </label>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Client Feedback *</label>
                <textarea
                  className="form-control"
                  placeholder="What did the client say about the visit / product?"
                  value={supportEnd.feedback}
                  onChange={e => {
                    const value = e.target.value
                    setSupportEnd(prev => prev && ({ ...prev, feedback: value }))
                  }}
                  rows={4}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setSupportEnd(null)} disabled={supportEnd.saving}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={saveSupportEndForm} disabled={supportEnd.saving || supportEnd.loading}>
                {supportEnd.saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Log Follow-up Modal */}
      {showFollowupModal && (
        <div className="modal-overlay" style={{ zIndex: 1300 }}>
          <div className="modal modal-lg">
            <div className="modal-header">
              <h2 className="modal-title">Log Visit Follow-up</h2>
              <button className="modal-close" onClick={() => setShowFollowupModal(false)}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleCreateFollowup}>
              <div className="modal-body">
                {error && <div className="alert alert-error">{error}</div>}

                <div className="form-group">
                  <label className="form-label">Academic Year *</label>
                  <select
                    className="form-control"
                    value={followupForm.academic_year || selectedAcademicYear}
                    onChange={e => setFollowupForm(f => ({ ...f, academic_year: e.target.value }))}
                    required
                    disabled
                  >
                    {academicYears.map(year => (
                      <option key={year.id} value={String(year.id)}>{year.name}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Follow-up Date *</label>
                  <input
                    className="form-control"
                    type="date"
                    value={followupForm.followup_date}
                    onChange={e => setFollowupForm(f => ({ ...f, followup_date: e.target.value }))}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Purpose of Next Visit *</label>
                  <textarea
                    className="form-control"
                    placeholder="Describe the purpose of the next visit..."
                    value={followupForm.next_purpose}
                    onChange={e => setFollowupForm(f => ({ ...f, next_purpose: e.target.value }))}
                    rows={3}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <textarea
                    className="form-control"
                    placeholder="Any additional notes / remarks..."
                    value={followupForm.notes}
                    onChange={e => setFollowupForm(f => ({ ...f, notes: e.target.value }))}
                    rows={2}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowFollowupModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : 'Save Follow-up'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Custom Camera Modal */}
      {showCamera && (
        <div className="modal-overlay" style={{ zIndex: 1200, background: 'rgba(15, 23, 42, 0.75)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div style={{ position: 'relative', width: '100%', maxWidth: '440px', display: 'flex', flexDirection: 'column', background: '#1e293b', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4)' }}>
            {/* Live Video Feed, Captured Preview, or details-only form */}
            {(capturedPhoto || detailsOnly) ? (
              <div style={{ display: 'flex', flexDirection: 'column', background: '#1e293b', color: '#fff', padding: '20px' }}>
                <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', alignItems: 'center' }}>
                  {capturedPhoto ? (
                    <img
                      src={capturedPhoto.dataUrl}
                      alt="Captured Preview"
                      style={{ width: '100px', height: '75px', objectFit: 'cover', borderRadius: '6px', border: '1.5px solid #fff' }}
                    />
                  ) : (
                    <div style={{
                      width: '100px', height: '75px', borderRadius: '6px', border: '1.5px dashed #64748b',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: 11, textAlign: 'center', padding: 6
                    }}>
                      No photo
                    </div>
                  )}
                  <div>
                    <h3 style={{ margin: 0, fontSize: '15px', color: '#fff' }}>
                      {cameraPurpose === 'school_start'
                        ? 'School Start'
                        : cameraPurpose === 'school_end'
                          ? 'School End'
                          : cameraPurpose === 'ride_end'
                            ? 'Reached School Photo'
                            : detailsOnly
                              ? (capturingVisitRef.current?.status === 'visited' ? 'Edit Review' : 'Visit Details')
                              : (capturedPhoto ? 'Photo Captured' : 'Visit Details')}
                    </h3>
                    <p style={{ margin: '3px 0 0 0', fontSize: '13px', color: '#94a3b8' }}>
                      {schoolName(capturingVisitRef.current?.area_name)}
                    </p>
                    {!capturedPhoto && (
                      <p style={{ margin: '6px 0 0 0', fontSize: '12px', color: '#94a3b8' }}>
                        Photo is optional
                      </p>
                    )}
                  </div>
                </div>

                {(cameraPurpose === 'school_start' || cameraPurpose === 'school_end' || cameraPurpose === 'ride_end') ? (
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      type="button"
                      onClick={discardPhoto}
                      className="btn btn-danger"
                      style={{ flex: 1, padding: '10px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontSize: '13px', fontWeight: 600, border: 'none', cursor: 'pointer' }}
                    >
                      <Trash2 size={14} /> Retake
                    </button>
                    {cameraPurpose === 'ride_end' && capturedPhoto && (
                      <button
                        type="button"
                        onClick={() => sendReachedSchoolWhatsApp(capturedPhoto)}
                        className="btn btn-primary"
                        style={{ flex: 1, padding: '10px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontSize: '13px', fontWeight: 600, border: 'none', cursor: 'pointer' }}
                      >
                        <Phone size={14} /> WhatsApp
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={saveAndUploadPhoto}
                      className="btn btn-success"
                      style={{ flex: 1, padding: '10px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontSize: '13px', fontWeight: 600, border: 'none', cursor: 'pointer' }}
                    >
                      <CheckCircle size={14} />
                      {cameraPurpose === 'school_start'
                        ? 'Confirm & Start'
                        : cameraPurpose === 'ride_end'
                          ? 'Confirm & Continue'
                          : 'Confirm & End'}
                    </button>
                  </div>
                ) : (photoOnly && capturedPhoto) ? (
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      type="button"
                      onClick={discardPhoto}
                      className="btn btn-danger"
                      style={{ flex: 1, padding: '10px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontSize: '13px', fontWeight: 600, border: 'none', cursor: 'pointer' }}
                    >
                      <Trash2 size={14} /> Retake
                    </button>
                    <button
                      type="button"
                      onClick={saveAndUploadPhoto}
                      className="btn btn-success"
                      style={{ flex: 1, padding: '10px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontSize: '13px', fontWeight: 600, border: 'none', cursor: 'pointer' }}
                    >
                      <CheckCircle size={14} /> Save Image
                    </button>
                  </div>
                ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '6px', color: '#cbd5e1' }}>
                      Client Interest Status *
                    </label>
                    <select
                      value={captureForm.interest_status}
                      onChange={(e) => setCaptureForm(prev => ({ ...prev, interest_status: e.target.value }))}
                      style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #475569', background: '#0f172a', color: '#fff', fontSize: '13px' }}
                    >
                      <option value="">Select status...</option>
                      <option value="interested">Interested</option>
                      <option value="not_interested">Not Interested</option>
                      <option value="followup">Followup</option>
                      <option value="go_to_main_branch">Go To Main Branch</option>
                    </select>
                  </div>

                  {captureForm.interest_status === 'interested' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {/* Contact Number */}
                      <div>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '6px', color: '#cbd5e1' }}>
                          Client Contact Number
                        </label>
                        <input
                          type="text"
                          placeholder="Enter 10-digit phone number..."
                          value={captureForm.contact_number}
                          onChange={(e) => setCaptureForm(prev => ({ ...prev, contact_number: e.target.value.replace(/\D/g, '') }))}
                          maxLength={10}
                          style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #475569', background: '#0f172a', color: '#fff', fontSize: '13px' }}
                        />
                      </div>
                      {/* Schools and college strength */}
                      <div className='form-group' style={{ flex: 1, minWidth: '200px'}}>
                        <label className='label'>School/College Strength</label>
                        <input
                          type="number"
                          name="strength"
                          value={captureForm.strength || ''}
                          onChange={(e) => setCaptureForm(prev => ({ ...prev, strength: e.target.value }))}
                          className='input'
                          placeholder='Total Number of Students'
                          style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #475569', background: '#0f172a', color: '#fff', fontSize: '13px' }}
                        />
                      </div>
                      {/* Interested Services Checkboxes */}
                      <div>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '6px', color: '#cbd5e1' }}>
                          Interested Services
                        </label>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                          {['ERP', 'Digital marketing', 'ID cards', 'web development', 'Biometric', ...masterServices].map(service => (
                            <label key={service} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#fff', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={captureForm.interested_services.includes(service)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setCaptureForm(prev => ({ ...prev, interested_services: [...prev.interested_services, service] }))
                                  } else {
                                    setCaptureForm(prev => ({ ...prev, interested_services: prev.interested_services.filter(s => s !== service) }))
                                  }
                                }}
                              />
                              {service}
                            </label>
                          ))}
                        </div>
                      </div>
                      {/* Other Interests Textarea */}
                      <div>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '6px', color: '#cbd5e1' }}>
                          Other Interests
                        </label>
                        <textarea
                          placeholder="Any other interests..."
                          value={captureForm.other_interests}
                          onChange={(e) => setCaptureForm(prev => ({ ...prev, other_interests: e.target.value }))}
                          style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #475569', background: '#0f172a', color: '#fff', fontSize: '13px', resize: 'vertical' }}
                          rows={2}
                        />
                      </div>
                      {/* Follow-up Date */}
                      <div>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '6px', color: '#cbd5e1' }}>
                          Follow-up Date
                        </label>
                        <input
                          type='date'
                          value={captureForm.followup_date || ''}
                          onChange={(e) => setCaptureForm(prev => ({ ...prev, followup_date: e.target.value }))}
                        />
                      </div>
                    </div>
                  )}

                  {captureForm.interest_status === 'not_interested' && (
                    <div>
                      <label style={{ display: 'block', fontsize: '12px', fontWeight: 600, marginBootom: '6px', color: '#cbd5e1' }}>
                        Reason for not interested
                      </label>
                      <textarea
                        placeholder="Please provide a reason..."
                        value={captureForm.not_interested_reason}
                        onChange={(e) => setCaptureForm(prev => ({ ...prev, not_interested_reason: e.target.value }))}
                        style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #475569', background: '#0f172a', color: '#fff', fontSize: '13px', resize: 'vertical' }}
                        rows={2}
                      />
                    </div>
                  )}

                  {captureForm.interest_status === 'go_to_main_branch' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <div style={{ position: 'relative' }}>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '6px', color: '#cbd5e1' }}>
                          Search Main Branch (school / College)
                        </label>
                        <div style={{ position: 'relative' }}>
                          <input
                            type="text"
                            placeholder='Search for main branch...'
                            value={captureForm.main_branch_search_query}
                            onChange={(e) => { setCaptureForm(prev => ({ ...prev, main_branch_search_query: e.target.value })); setShowBranchDropdown(true) }}
                            onFocus={() => {
                              if (captureForm.main_branch_search_query?.length >= 2) {
                                setShowBranchDropdown(true)
                              }
                            }}
                            style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #475569', background: '#0f172a', color: "#fff", fontsize: '13px' }}
                          />
                          {fetchingBranchSuggestions && (
                            <div style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '11px', color: "#94a3b8" }}>
                              Searching...
                            </div>
                          )}
                        </div>

                        {showBranchDropdown && branchSuggestions.length > 0 && (
                          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, background: '#1e293b', border: '1px solid #475569', borderRadius: '6px', marginTop: '4px', maxHeight: '190px', overflowY: 'auto', boxShadow: '0 10px 25px rgba(0,0,0,0.5)' }}>
                            {branchSuggestions.map((item, idx) => (
                              <div key={item.place_id || idx} onClick={() => {
                                setCaptureForm(prev => ({ ...prev, main_branch_search_query: item.main_text || item.description?.split(',')[0] }))
                                setShowBranchDropdown(false)
                              }} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '8px, 12px', cursor: 'pointer', borderBottom: idx < branchSuggestions.length - 1 ? '1px solid #334155' : 'none', transition: 'background 0.15s' }}
                                onMouseEnter={(e) => e.currentTarget.style.background = '#334155'}
                                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                              >
                                <MapPin size={16} style={{ color: '#38bdf8', marginTop: '2px', flexShrink: 0 }} />
                                <div style={{ overflow: 'hidden' }}>
                                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#f8fafc', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {item.main_text || item.description?.split(',')[0]}
                                  </div>
                                  <div style={{ fontSize: '11px', color: '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {item.secondary_text || item.description}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '6px', color: '#cbd5e1' }}>
                          Next Visit Plan Date
                        </label>
                        <input
                          type='date'
                          value={captureForm.next_visit_plan_date}
                          onChange={(e) => setCaptureForm(prev => ({ ...prev, next_visit_plan_date: e.target.value }))}
                        />
                      </div>
                    </div>
                  )}

                  {captureForm.interest_status === 'followup' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '6px', color: '#cdb5e1' }}>
                          Follow-up Date
                        </label>
                        <input
                          type='date'
                          value={captureForm.followup_date}
                          onChange={(e) => setCaptureForm(prev => ({ ...prev, followup_date: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '6px', color: '#cbd5e1' }}>
                          Purpose of next visit
                        </label>
                        <textarea
                          placeholder="Describe the purpose..."
                          value={captureForm.followup_purpose}
                          onChange={(e) => setCaptureForm(prev => ({ ...prev, followup_purpose: e.target.value }))}
                          style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #475569', background: '#0f172a', color: '#fff', fontSize: '13px', resize: 'vertical' }}
                          rows={3}
                        />
                      </div>
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
                    {capturedPhoto ? (
                      <button
                        type="button"
                        onClick={discardPhoto}
                        className="btn btn-danger"
                        style={{ flex: 1, padding: '10px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontSize: '13px', fontWeight: 600, border: 'none', cursor: 'pointer' }}
                      >
                        <Trash2 size={14} /> Discard & Retake
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          setDetailsOnly(false)
                          setCapturedPhoto(null)
                        }}
                        className="btn btn-secondary"
                        style={{ flex: 1, padding: '10px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontSize: '13px', fontWeight: 600, border: 'none', cursor: 'pointer' }}
                      >
                        <Camera size={14} /> Add Photo
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={saveAndUploadPhoto}
                      className="btn btn-success"
                      disabled={!captureForm.interest_status}
                      style={{ flex: 1, padding: '10px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontSize: '13px', fontWeight: 600, border: 'none', cursor: 'pointer' }}
                    >
                      <CheckCircle size={14} /> Submit Details
                    </button>
                  </div>
                </div>
                )}
              </div>
            ) : (
              <div style={{ position: 'relative', width: '100%', aspectRatio: '4/3', background: '#000', overflow: 'hidden' }}>
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
                {(cameraPurpose === 'school_start' || cameraPurpose === 'school_end' || cameraPurpose === 'ride_end') && (
                  <div style={{
                    position: 'absolute',
                    top: 12,
                    left: 12,
                    right: 56,
                    background: 'rgba(15, 23, 42, 0.75)',
                    color: '#fff',
                    padding: '8px 12px',
                    borderRadius: 8,
                    fontSize: 13,
                    fontWeight: 600,
                    zIndex: 10
                  }}>
                    {cameraPurpose === 'school_start'
                      ? 'Optional photo — then start school time'
                      : cameraPurpose === 'ride_end'
                        ? 'Optional photo — then school time starts'
                        : 'Optional photo — then end school visit'}
                  </div>
                )}
                {/* <input 
                    type="file" 
                    accept="image/*, video/*" 
                    onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                            setCapturedPhoto({ blob: file, dataUrl: URL.createObjectURL(file) });
                        }
                    }} 
              /> */}

                {/* Top Close Button */}
                <button
                  onClick={handleCancelCamera}
                  style={{
                    position: 'absolute',
                    top: '12px',
                    right: '12px',
                    background: 'rgba(0,0,0,0.5)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '36px',
                    height: '36px',
                    color: '#fff',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10
                  }}
                  title="Close Camera"
                >
                  <X size={20} />
                </button>

                {/* Bottom Controls */}
                <div style={{
                  position: 'absolute',
                  bottom: '20px',
                  left: 0,
                  right: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 12,
                  zIndex: 10
                }}>
                  <button
                    onClick={takePhoto}
                    style={{
                      width: '64px',
                      height: '64px',
                      borderRadius: '50%',
                      background: '#fff',
                      border: '5px solid rgba(255,255,255,0.3)',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
                      transition: 'transform 0.1s ease'
                    }}
                    onMouseDown={e => e.currentTarget.style.transform = 'scale(0.92)'}
                    onMouseUp={e => e.currentTarget.style.transform = 'scale(1)'}
                    onTouchStart={e => e.currentTarget.style.transform = 'scale(0.92)'}
                    onTouchEnd={e => e.currentTarget.style.transform = 'scale(1)'}
                    title="Capture Photo"
                  >
                    <div style={{ width: '46px', height: '46px', borderRadius: '50%', background: '#fff', border: '1.5px solid #000' }} />
                  </button>
                  {(cameraPurpose === 'media' || cameraPurpose === 'ride_end' || cameraPurpose === 'school_start' || cameraPurpose === 'school_end') && (
                    <button
                      type="button"
                      onClick={async () => {
                        if (cameraPurpose === 'ride_end') {
                          closeCameraStream()
                          setCameraPurpose('media')
                          const pending = pendingRideEndRef.current
                          pendingRideEndRef.current = null
                          if (pending) await finalizeEndRideAndStartSchool(pending.visit, pending.payload)
                          capturingVisitRef.current = null
                          return
                        }
                        if (cameraPurpose === 'school_start') {
                          closeCameraStream()
                          setCameraPurpose('media')
                          await completeSchoolStart(capturingVisitRef.current)
                          capturingVisitRef.current = null
                          return
                        }
                        if (cameraPurpose === 'school_end') {
                          closeCameraStream()
                          setCameraPurpose('media')
                          const v = capturingVisitRef.current
                          const ok = await completeSchoolEnd(v)
                          if (ok) await openPostVisitForm(v)
                          return
                        }
                        continueWithoutPhoto()
                      }}
                      style={{
                        background: 'rgba(15, 23, 42, 0.8)',
                        color: '#fff',
                        border: '1px solid rgba(255,255,255,0.25)',
                        borderRadius: 8,
                        padding: '8px 14px',
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: 'pointer'
                      }}
                    >
                      {cameraPurpose === 'media' ? 'Continue without photo' : 'Skip photo'}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Media Modal */}
      {showMediaModal && selectedVisit && (
        <div className="modal-overlay" style={{ zIndex: 1300 }}>
          <div className="modal modal-lg">
            <div className="modal-header">
              <h2 className="modal-title">
                <Image size={18} style={{ display: 'inline', marginRight: 8 }} />
                Media — {schoolName(selectedVisit.area_name)}
              </h2>
              <button className="modal-close" onClick={() => setShowMediaModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)' }}>
                  Media Files ({media.length})
                </span>
              </div>

              {media.length === 0 ? (
                <div className="empty-state" style={{ padding: '24px 0' }}>
                  <Image size={40} />
                  <h3>No media uploaded</h3>
                  <p>Capture photos for this visit</p>
                </div>
              ) : (
                <div className="media-gallery" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))' }}>
                  {media.map(item => (
                    <div className="media-item" key={item.id} style={{
                      display: 'flex',
                      flexDirection: 'column',
                      background: '#fff',
                      border: '1px solid var(--border-color)',
                      borderRadius: 'var(--radius-md)',
                      overflow: 'hidden',
                      width: '100%',
                      boxShadow: 'var(--shadow-sm)',
                      aspectRatio: 'auto',
                      height: 'auto'
                    }}>
                      {/* Image section */}
                      <div style={{ position: 'relative', width: '100%', height: '120px', overflow: 'hidden' }}>
                        {item.media_type === 'photo' ? (
                          <img
                            src={getMediaUrl(item)}
                            alt={item.caption || 'Photo'}
                            onClick={() => setPreviewMedia(item)}
                            style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }}
                            title="Click to view full image"
                          />
                        ) : (
                          <video
                            src={getMediaUrl(item)}
                            controls
                            style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }}
                          />
                        )}
                        <span className="media-type-badge">
                          {item.media_type === 'video' ? <Video size={10} /> : <Image size={10} />}
                          {' '}{item.media_type}
                        </span>
                      </div>

                      {/* Location section */}
                      <div style={{
                        padding: '8px',
                        background: '#f8fafc',
                        borderTop: '1px solid #e2e8f0',
                        fontSize: '11px',
                        color: '#334155',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '2px',
                        flexGrow: 1
                      }}>
                        {item.latitude && item.longitude ? (
                          <>
                            <span style={{ fontWeight: 700, color: '#1e293b' }}>
                              🏢 School/College Name:
                            </span>
                            <div style={{ color: '#475569', marginBottom: '4px' }}>{schoolName(selectedVisit.area_name)}</div>

                            <span style={{ fontWeight: 700, color: '#1e293b' }}>
                              📍 Place Name:
                            </span>
                            <div style={{ color: '#475569', marginBottom: '4px' }}>{item.location_name || schoolName(selectedVisit.area_name)}</div>

                            <a
                              href={`https://www.google.com/maps/search/?api=1&query=${item.latitude},${item.longitude}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: '#2563eb',
                                textDecoration: 'underline',
                                fontWeight: 600,
                                marginTop: '4px',
                                display: 'inline-block'
                              }}
                            >
                              Open in Maps
                            </a>
                          </>
                        ) : (
                          <span style={{ color: '#64748b', fontStyle: 'italic' }}>📍 No Location Data</span>
                        )}
                      </div>

                      {/* Action buttons (Delete & Save) */}
                      <div style={{
                        padding: '6px 8px 8px 8px',
                        background: '#f8fafc',
                        borderTop: '1px solid #e2e8f0',
                        display: 'flex',
                        gap: '6px',
                        justifyContent: 'space-between'
                      }}>
                        <button
                          onClick={() => handleDeleteMedia(item.id)}
                          className="btn btn-danger btn-sm"
                          style={{
                            padding: '4px 8px',
                            fontSize: '11px',
                            borderRadius: '4px',
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px',
                            border: 'none',
                            cursor: 'pointer'
                          }}
                          title="Delete image from server"
                        >
                          <Trash2 size={11} /> Delete
                        </button>
                        <button
                          onClick={() => downloadImage(getMediaUrl(item), `visit_${selectedVisit.id}_media_${item.id}.jpg`)}
                          className="btn btn-success btn-sm"
                          style={{
                            padding: '4px 8px',
                            fontSize: '11px',
                            borderRadius: '4px',
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px',
                            border: 'none',
                            cursor: 'pointer'
                          }}
                          title="Save image to device"
                        >
                          <CheckCircle size={11} /> Download
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowMediaModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox Preview Modal */}
      {previewMedia && (
        <div className="modal-overlay" style={{ zIndex: 1300, background: 'rgba(0,0,0,0.85)' }} onClick={() => setPreviewMedia(null)}>
          <div style={{ position: 'relative', maxWidth: '90%', maxHeight: '90%' }} onClick={e => e.stopPropagation()}>
            <button
              onClick={() => setPreviewMedia(null)}
              style={{
                position: 'absolute',
                top: '-40px',
                right: '0',
                background: 'none',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '14px',
                fontWeight: 600
              }}
            >
              <X size={20} /> Close
            </button>
            {previewMedia.media_type === 'photo' ? (
              <img
                src={getMediaUrl(previewMedia)}
                alt={previewMedia.caption || 'Preview'}
                style={{ maxWidth: '100%', maxHeight: '75vh', borderRadius: '8px', border: '2px solid white', display: 'block', margin: '0 auto' }}
              />
            ) : (
              <video
                src={getMediaUrl(previewMedia)}
                controls
                autoPlay
                style={{ maxWidth: '100%', maxHeight: '75vh', borderRadius: '8px', border: '2px solid white', display: 'block', margin: '0 auto' }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  )
}
